import React, { useEffect, useState } from 'react'
import bcrypt from 'bcryptjs'
import Swal from 'sweetalert2'
import { supabase } from '../../supabaseClient'
import Profile from '../Profile'
import { Helmet } from 'react-helmet'
import { getAuthItem } from '../../utils/auth'
import { useBarista } from '../../contexts/BaristaContext'
import ReceiptLayout from './ReceiptLayout'
import { buildReceiptBytes, buildStickerBytes } from './printerUtils'
import { autoRotateCashDrawer } from '../../utils/cashDrawerHandoff'

import { useNetworkStatus } from '../../offline/useNetworkStatus'
import { offlineDB } from '../../offline/db'
import { enqueueAction, resolveId } from '../../offline/syncQueue'

import {
    getStaffContext,
    getBranchStaffContext,
    refreshOfflineCaches,
    validateStockOffline,
    commitOfflineConsumption,
    buildReservationRowsOffline,
    checkLoyaltyByEmailOffline,
    getEligibleVouchersOffline,
    refreshOrdersCacheForReceiptNumbers,
    getNextOfflineReceiptNumberFromMeta,
    setLastKnownReceiptNumber,
    openCashDrawerOffline,
    logCashDrawerTransactionOffline,
    closeCashDrawerOffline,
    getOpenCashDrawerSessionOffline,
    getCashDrawerTransactionsOffline,
    cacheCashDrawerSession,
    cacheCashDrawerTransactions,
    getActiveRemakeRequestsOffline,
    claimRemakeRequestOffline,
    releaseRemakeRequestOffline,
    completeRemakeRequestOffline,
    cacheOrderItemsForOrders,
} from '../../offline/posOfflineHelpers'
import { usePendingSyncCount } from '../../offline/usePendingSyncCount'


import { v4 as uuidv4 } from 'uuid'
import { authHeaders } from '../../utils/authFetch'

function DiscountModal({
    subtotal,
    total,
    cartItems,
    menuItems,
    allBranchItems,
    appliedDiscount,
    setAppliedDiscount,
    setShowDiscountModal,
    loyaltyCustomer,
    setLoyaltyCustomer,
    loyaltyQuery,
    setLoyaltyQuery,
    loyaltyResult,
    setLoyaltyResult,
    emailCheckDone,
    setEmailCheckDone,
    staffBranchId,
    isOnline,
    userData,
}) {
    const payableStatuses = new Set(['active', 'void_rejected'])
    const [activeTab, setActiveTabDM] = useState('pwd')

    // PWD/SC state
    const [pwdType, setPwdType] = useState('senior')
    const [pwdIdNumber, setPwdIdNumber] = useState('')
    const [selectedPwdKeys, setSelectedPwdKeys] = useState(new Set())

    // Promo state
    const [promoInput, setPromoInput] = useState('')
    const [promoResult, setPromoResult] = useState(null) // { valid, rate, label } | null
    const [checkingPromo, setCheckingPromo] = useState(false)


    // { customerId, loyaltyId, name, membershipNumber, tier, pointsBalance, maxPesoDiscount }
    const [loyaltyLoading, setLoyaltyLoading] = useState(false)
    const [loyaltyError, setLoyaltyError] = useState('')
    const [voucherList, setVoucherList] = useState([])
    const [voucherLoading, setVoucherLoading] = useState(false)
    const [voucherError, setVoucherError] = useState('')

    // const [emailCheckDone, setEmailCheckDone] = useState(false)
    const [emailCheckLoading, setEmailCheckLoading] = useState(false)
    const [signupFirstName, setSignupFirstName] = useState('')
    const [signupLastName, setSignupLastName] = useState('')
    const [signupBirthday, setSignupBirthday] = useState('')
    const [signupLoading, setSignupLoading] = useState(false)

    useEffect(() => {
        if (activeTab === 'vouchers' && loyaltyCustomer) handleFetchVouchers()
    }, [activeTab])

    useEffect(() => {
        const handleSyncComplete = () => {
            if (activeTab === 'vouchers' && loyaltyCustomer) handleFetchVouchers()
        }
        window.addEventListener('kaperk:sync-complete', handleSyncComplete)
        return () => window.removeEventListener('kaperk:sync-complete', handleSyncComplete)
    }, [activeTab, loyaltyCustomer])

    const SC_PWD_RATE = 0.20

    const payableCartItems = cartItems.filter(c => payableStatuses.has(c.line_status || 'active'))

    const pwdSelectedSubtotal = payableCartItems.reduce((sum, c) => {
        if (!selectedPwdKeys.has(c.cartKey)) return sum
        return sum + c.unitPrice * (parseInt(c.qty, 10) || 0)
    }, 0)
    const pwdDiscountPreview = pwdSelectedSubtotal * SC_PWD_RATE
    const pwdNewTotalPreview = subtotal - pwdDiscountPreview

    const togglePwdItem = (cartKey) => {
        setSelectedPwdKeys(prev => {
            const next = new Set(prev)
            next.has(cartKey) ? next.delete(cartKey) : next.add(cartKey)
            return next
        })
    }

    const handleApplyPwdPerItem = () => {
        if (!pwdIdNumber.trim()) {
            Swal.fire({ icon: 'warning', title: 'ID Required', text: 'Please enter the SC/PWD card number.', confirmButtonColor: '#A0826D' })
            return
        }
        if (selectedPwdKeys.size === 0) {
            Swal.fire({ icon: 'warning', title: 'No Items Selected', text: 'Please select at least one item to apply the discount to.', confirmButtonColor: '#A0826D' })
            return
        }
        setAppliedDiscount({
            type: 'pwd',
            discountType: pwdType,
            idNumber: pwdIdNumber.trim(),
            rate: SC_PWD_RATE,
            label: pwdType === 'senior' ? 'Senior Citizen (20%)' : 'PWD (20%)',
            selectedCartKeys: selectedPwdKeys,
        })

        setShowDiscountModal(false)
    }

    const LOCAL_PROMO_CODES = {
        CANITCODE: { rate: 0.5, label: '50% off' },
        KAPERK10: { rate: 0.10, label: '10% off' },
        BARISTA20: { rate: 0.20, label: '20% off' },
    }

    const handleCheckPromo = () => {
        const key = promoInput.trim().toUpperCase()
        const found = LOCAL_PROMO_CODES[key]
        setPromoResult(found ? { valid: true, code: key, ...found } : { valid: false })
    }

    // ── Loyalty lookup ────────────────────────────────────────────────────────
    const handleLookupLoyalty = async () => {
        setLoyaltyLoading(true)
        setLoyaltyError('')
        setLoyaltyResult(null)
        try {
            const q = loyaltyQuery.trim()
            if (!q) { setLoyaltyError('Enter an email or membership number.'); return }

            // Try to find customer by email first, then fall back to membership number
            let loyaltyData = null

            // Check if input looks like an email
            const isEmail = q.includes('@')

            if (isEmail) {
                const { data: customerData } = await supabase
                    .from('customers')
                    .select('user_id')
                    .eq('email', q)
                    .maybeSingle()

                if (customerData) {
                    const { data, error } = await supabase
                        .from('loyalty_accounts')
                        .select('loyalty_id, user_id, membership_number, points_balance, is_active')
                        .eq('user_id', customerData.user_id)
                        .eq('is_active', true)
                        .maybeSingle()
                    if (error) throw error
                    loyaltyData = data
                }
            } else {
                const { data, error } = await supabase
                    .from('loyalty_accounts')
                    .select('loyalty_id, user_id, membership_number, points_balance, is_active')
                    .eq('membership_number', q)
                    .eq('is_active', true)
                    .maybeSingle()
                if (error) throw error
                loyaltyData = data
            }

            if (!loyaltyData) { setLoyaltyError('No active loyalty account found.'); return }

            const { data: cust } = await supabase
                .from('customers')
                .select('first_name, last_name, email')
                .eq('user_id', loyaltyData.user_id)
                .maybeSingle()

            const customerName = cust ? `${cust.first_name} ${cust.last_name}` : 'Customer'
            const pointsBalance = Number(loyaltyData.points_balance || 0)

            // Show confirmation Swal before setting result
            const confirm = await Swal.fire({
                icon: 'question',
                title: 'Confirm customer',
                html: `
                        <div style="text-align:left; font-size:14px; line-height:1.7">
                            <div style="font-weight:600; font-size:15px; margin-bottom:6px">${customerName}</div>
                            <div style="color:#888; margin-bottom:2px">${cust?.email || ''}</div>
                            <div style="color:#888; margin-bottom:10px">Membership: ${loyaltyData.membership_number}</div>
                            <div style="background:#FEF9EF; border:1px solid #F0D080; border-radius:8px; padding:10px 14px; display:flex; justify-content:space-between; align-items:center">
                                <span style="color:#555">Points balance</span>
                                <span style="font-weight:600; color:#B45309; font-size:15px">${pointsBalance.toLocaleString()} pts</span>
                            </div>
                        </div>
                    `,
                showCancelButton: true,
                confirmButtonText: 'Yes, this is the customer',
                cancelButtonText: 'Wrong customer',
                confirmButtonColor: '#303030',
                cancelButtonColor: '#9CA3AF',
            })

            if (!confirm.isConfirmed) return

            setLoyaltyResult({
                customerId: loyaltyData.user_id,
                loyaltyId: loyaltyData.loyalty_id,
                name: customerName,
                membershipNumber: loyaltyData.membership_number,
                pointsBalance,
            })
        } catch (err) {
            setLoyaltyError(err.message || 'Lookup failed.')
        } finally {
            setLoyaltyLoading(false)
        }
    }

    // ── Voucher eligibility + preview (mirrors CartScreen.jsx logic) ──────────
    const getEligibleVoucherTotal = (voucher) => {
        const scope = voucher.applicationScope || 'all'
        if (scope === 'all') return subtotal

        if (scope === 'category') {
            return payableCartItems.reduce((sum, c) => {
                const menuItem = menuItems.find(m => String(m.item_id) === String(c.item_id))
                    || allBranchItems.find(m => String(m.item_id) === String(c.item_id))
                return menuItem?.category === voucher.appliesToCategory
                    ? sum + c.unitPrice * (parseInt(c.qty, 10) || 0) : sum
            }, 0)
        }

        if (scope === 'specific_items') {
            const eligibleItemIds = (voucher.eligibleItems || []).map(ei => ei.item_id)
            const eligibleVariantIds = (voucher.eligibleItems || []).map(ei => ei.variant_id).filter(Boolean)
            return payableCartItems.reduce((sum, c) => {
                const matches = eligibleItemIds.includes(c.item_id)
                    || (c.variantObjects || []).some(v => eligibleVariantIds.includes(v.variant_id))
                return matches ? sum + c.unitPrice * (parseInt(c.qty, 10) || 0) : sum
            }, 0)
        }
        return 0
    }

    const getVoucherDiscountPreview = (voucher) => {
        if (voucher.rewardValueType === 'bonus_points') return 0
        const eligibleTotal = getEligibleVoucherTotal(voucher)
        if (eligibleTotal <= 0) return 0
        let amount = voucher.rewardValueType === 'fixed_amount'
            ? Math.min(Number(voucher.rewardValue) || 0, eligibleTotal)
            : eligibleTotal * (Number(voucher.rewardValue) / 100)
        if (voucher.maxDiscountAmount) amount = Math.min(amount, Number(voucher.maxDiscountAmount))
        return amount
    }

    const isVoucherUsable = (voucher) => {
        if (!voucher.eligible) return false
        const meetsMinimumSpend = subtotal >= (Number(voucher.minimumSpend) || 0)
        const scope = voucher.applicationScope || 'all'
        const hasEligibleItem = scope === 'all' || getEligibleVoucherTotal(voucher) > 0
        return meetsMinimumSpend && hasEligibleItem
    }

    const bestVoucherRewardId = voucherList.reduce((bestId, v) => {
        if (!isVoucherUsable(v)) return bestId
        const amount = getVoucherDiscountPreview(v)
        const bestAmount = bestId ? getVoucherDiscountPreview(voucherList.find(x => x.rewardId === bestId)) : -1
        return amount > 0 && amount > bestAmount ? v.rewardId : bestId
    }, null)

    // Long-press detail view
    const [viewingVoucher, setViewingVoucher] = useState(null)
    const longPressTimerRef = React.useRef(null)
    const startLongPress = (voucher) => { longPressTimerRef.current = setTimeout(() => setViewingVoucher(voucher), 450) }
    const cancelLongPress = () => { if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current) }

    const handleFetchVouchers = async () => {
        if (!loyaltyCustomer) return
        setVoucherLoading(true)
        setVoucherError('')
        try {
            if (!isOnline) {
                const offlineVouchers = await getEligibleVouchersOffline(loyaltyCustomer, staffBranchId)
                setVoucherList(offlineVouchers)
                return
            }
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
            const res = await fetch(
                `${API_BASE_URL}/api/pos/loyalty/${loyaltyCustomer.loyaltyId}/vouchers?branchId=${staffBranchId}`,
                { headers: authHeaders() }
            )
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Failed to load vouchers')
            setVoucherList(data.vouchers || [])

            // Mirror OrderManagement's fetchOrders: refresh the offline cache on
            // every successful online fetch, not just once at login, so campaigns
            // that go live mid-shift are still there once the connection drops.
            await offlineDB.rewardsCache.where('branch_id').equals(staffBranchId).delete()
            await offlineDB.rewardsCache.bulkPut((data.vouchers || []).map(v => ({
                reward_id: v.rewardId,
                branch_id: staffBranchId,
                rewardName: v.rewardName,
                description: v.description,
                campaignId: v.campaignId,
                campaignName: v.campaignName,
                rewardValueType: v.rewardValueType,
                rewardValue: v.rewardValue,
                maxDiscountAmount: v.maxDiscountAmount,
                applicationScope: v.applicationScope,
                appliesToCategory: v.appliesToCategory,
                minimumSpend: v.minimumSpend,
                eligibleItems: v.eligibleItems || [],
                rules: [], // the API resolves eligibility server-side and doesn't expose raw rules; offline mode falls back to "eligible unless already redeemed this outage" via offlineRedemptions
            })))
        } catch (err) {
            // Live fetch failed (flaky connection) — same fallback pattern OrderManagement uses
            try {
                const offlineVouchers = await getEligibleVouchersOffline(loyaltyCustomer, staffBranchId)
                setVoucherList(offlineVouchers)
            } catch {
                setVoucherError(err.message)
            }
        } finally {
            setVoucherLoading(false)
        }
    }

    const handleApplyVoucher = async (voucher) => {
        setAppliedDiscount({
            type: 'campaign_voucher',
            rewardId: voucher.rewardId,
            campaignId: voucher.campaignId,
            label: voucher.rewardName,
            rewardValueType: voucher.rewardValueType,
            rewardValue: voucher.rewardValue,
            maxDiscountAmount: voucher.maxDiscountAmount,
            applicationScope: voucher.applicationScope,
            appliesToCategory: voucher.appliesToCategory,
        })
        if (!isOnline) {
            await offlineDB.offlineRedemptions.add({
                reward_id: voucher.rewardId,
                loyaltyKey: loyaltyCustomer.loyaltyId,
                redeemed_at: new Date().toISOString(),
            })
        }

        setShowDiscountModal(false)
    }

    const handleCheckEmail = async () => {
        setEmailCheckLoading(true)
        setLoyaltyError('')
        setLoyaltyResult(null)
        setEmailCheckDone(false)
        try {
            const q = loyaltyQuery.trim()
            if (!q.includes('@')) { setLoyaltyError('Enter a valid email address.'); return }

            if (!isOnline) {
                const cached = await checkLoyaltyByEmailOffline(q)
                setEmailCheckDone(true)
                if (cached) {
                    setLoyaltyResult({
                        customerId: cached.user_id,
                        loyaltyId: cached.loyalty_id,
                        name: cached.name,
                        membershipNumber: cached.membership_number,
                        pointsBalance: cached.points_balance,
                    })
                }
                return
            }

            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
            const res = await fetch(`${API_BASE_URL}/api/pos/loyalty/check?email=${encodeURIComponent(q)}`, { headers: authHeaders() })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Check failed')

            setEmailCheckDone(true)
            if (data.exists) {
                setLoyaltyResult({
                    customerId: data.customerId,
                    loyaltyId: data.loyaltyId,
                    name: data.name,
                    membershipNumber: data.membershipNumber,
                    pointsBalance: data.pointsBalance,
                })
            }
        } catch (err) {
            setLoyaltyError(err.message || 'Check failed.')
        } finally {
            setEmailCheckLoading(false)
        }
    }

    const handleQuickSignup = async () => {
        setSignupLoading(true)
        try {
            const email = loyaltyQuery.trim()

            if (!isOnline) {
                const localCustomerKey = `local_customer_${uuidv4()}`
                const localLoyaltyKey = `local_loyalty_${uuidv4()}`
                const placeholderHash = bcrypt.hashSync(uuidv4(), 8)

                await enqueueAction('create_loyalty_account', {
                    localCustomerKey,
                    localLoyaltyKey,
                    customer: {
                        email,
                        first_name: 'Guest',
                        last_name: 'Customer',
                        birthday: '2000-01-01',
                        password_hash: placeholderHash,
                    },
                })

                await offlineDB.customerLoyaltyCache.put({
                    email, user_id: localCustomerKey, loyalty_id: localLoyaltyKey,
                    membership_number: 'Pending sync', points_balance: 0,
                    name: 'Guest Customer',
                })

                setLoyaltyCustomer({
                    loyaltyId: localLoyaltyKey,
                    customerId: localCustomerKey,
                    name: 'Guest Customer',
                    membershipNumber: 'Pending sync',
                })
                setShowDiscountModal(false)
                Swal.fire({ icon: 'info', title: 'Account Queued', text: 'No connection — this account and its points will sync once wifi returns.', confirmButtonColor: '#A0826D', timer: 2500 })
                return
            }

            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
            const res = await fetch(`${API_BASE_URL}/api/pos/loyalty/quick-signup`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ email }),
            })

            const contentType = res.headers.get('content-type') || ''
            if (!contentType.includes('application/json')) {
                const text = await res.text()
                console.error('Non-JSON response from quick-signup:', text.slice(0, 300))
                throw new Error(`Server returned ${res.status} — check that ${API_BASE_URL}/api/pos/loyalty/quick-signup exists and REACT_APP_API_URL is set correctly.`)
            }

            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Signup failed')

            setLoyaltyCustomer({ loyaltyId: data.loyaltyId, customerId: data.customerId, name: data.name, membershipNumber: data.membershipNumber })
            setShowDiscountModal(false)
            Swal.fire({ icon: 'success', title: 'Account Created', text: `Enrolled with ${email}. They can finish setting up their profile once they download the app.`, confirmButtonColor: '#A0826D', timer: 2500 })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Signup Failed', text: err.message, confirmButtonColor: '#FF6F3C' })
        } finally {
            setSignupLoading(false)
        }
    }

    // ── Apply handlers ────────────────────────────────────────────────────────
    const handleApplyPwd = () => {
        if (!pwdIdNumber.trim()) {
            Swal.fire({ icon: 'warning', title: 'ID Required', text: 'Please enter the SC/PWD card number.', confirmButtonColor: '#A0826D' })
            return
        }
        setAppliedDiscount({
            type: 'pwd',
            discountType: pwdType,
            idNumber: pwdIdNumber.trim(),
            rate: SC_PWD_RATE,
            label: pwdType === 'senior' ? 'Senior Citizen (20%)' : 'PWD (20%)',
        })
        setShowDiscountModal(false)
    }

    const handleApplyPromo = () => {
        if (!promoResult?.valid) return
        setAppliedDiscount({
            type: 'promo',
            code: promoResult.code,
            rate: promoResult.rate,
            label: promoResult.code,
        })
        setShowDiscountModal(false)
    }



    return (
        <>
            <div className="fixed inset-0 z-[70] flex items-center justify-center">
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowDiscountModal(false)} />
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[90vh]">

                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                        <div>
                            <h2 className="text-base font-semibold text-gray-800">Apply Discount / Earn Points</h2>
                            <p className="text-xs text-gray-400 mt-0.5">Subtotal: ₱{subtotal.toFixed(2)}</p>
                        </div>
                        <button onClick={() => setShowDiscountModal(false)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-sm">✕</button>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b border-gray-100">
                        {[
                            { key: 'pwd', label: 'Senior / PWD' },
                            { key: 'loyalty', label: 'Loyalty points' },
                            { key: 'vouchers', label: 'Campaign Vouchers' },
                        ].map(tab => (
                            <button key={tab.key} onClick={() => setActiveTabDM(tab.key)}
                                className={`flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-[#303030] text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="overflow-y-auto flex-1 p-6">

                        {/* ── Senior / PWD Tab ── */}
                        {activeTab === 'pwd' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-2">Discount type</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {[{ v: 'senior', l: 'Senior Citizen' }, { v: 'pwd', l: 'PWD' }].map(opt => (
                                            <button key={opt.v} onClick={() => setPwdType(opt.v)}
                                                className={`py-2 rounded-lg border text-xs font-medium transition-colors ${pwdType === opt.v ? 'bg-[#303030] border-[#303030] text-white' : 'bg-white border-gray-300 text-gray-700'}`}>
                                                {opt.l}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1.5">SC/PWD ID number <span className="text-red-500">*</span></label>
                                    <input type="text" value={pwdIdNumber} onChange={e => setPwdIdNumber(e.target.value)}
                                        placeholder="Card or ID number"
                                        maxLength={20}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A687]" />
                                    <p className="text-xs text-gray-400 mt-1">{pwdIdNumber.length}/20 characters</p>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-xs font-medium text-gray-600">Select items to discount</label>
                                        <button
                                            onClick={() => {
                                                if (selectedPwdKeys.size === payableCartItems.length) {
                                                    setSelectedPwdKeys(new Set())
                                                } else {
                                                    setSelectedPwdKeys(new Set(payableCartItems.map(c => c.cartKey)))
                                                }
                                            }}
                                            className="text-xs text-[#A0826D] hover:underline"
                                        >
                                            {selectedPwdKeys.size === payableCartItems.length ? 'Deselect all' : 'Select all'}
                                        </button>
                                    </div>
                                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                        {payableCartItems.length === 0 ? (
                                            <p className="text-xs text-gray-400 text-center py-4">No payable items in cart.</p>
                                        ) : (
                                            payableCartItems.map(c => {
                                                const isChecked = selectedPwdKeys.has(c.cartKey)
                                                const itemTotal = c.unitPrice * (parseInt(c.qty, 10) || 0)
                                                return (
                                                    <label key={c.cartKey}
                                                        className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${isChecked ? 'border-[#C9A687] bg-[#C9A687]/5' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={() => togglePwdItem(c.cartKey)}
                                                            className="mt-0.5 accent-[#A0826D]"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-gray-800 leading-tight">{c.name}</p>
                                                            {c.variants?.length > 0 && <p className="text-xs text-gray-400">{c.variants.join(' · ')}</p>}
                                                            {c.modifiers?.length > 0 && <p className="text-xs text-[#A0826D]">+{c.modifiers.join(', ')}</p>}
                                                        </div>
                                                        <div className="text-right flex-shrink-0">
                                                            <p className="text-xs font-semibold text-gray-700">₱{itemTotal.toFixed(2)}</p>
                                                            <p className="text-xs text-gray-400">qty {c.qty}</p>
                                                            {isChecked && (
                                                                <p className="text-xs text-green-600 font-medium mt-0.5">-₱{(itemTotal * SC_PWD_RATE).toFixed(2)}</p>
                                                            )}
                                                        </div>
                                                    </label>
                                                )
                                            })
                                        )}
                                    </div>
                                </div>

                                <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                                    <div className="flex justify-between text-xs">
                                        <span className="text-gray-500">Selected items subtotal</span>
                                        <span className="text-gray-700">₱{pwdSelectedSubtotal.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-xs">
                                        <span className="text-gray-500">Discount (20%)</span>
                                        <span className="font-medium text-gray-800">-₱{pwdDiscountPreview.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm border-t border-gray-200 pt-2 font-semibold">
                                        <span className="text-gray-700">New total</span>
                                        <span className="text-green-600">₱{pwdNewTotalPreview.toFixed(2)}</span>
                                    </div>
                                </div>

                                <button onClick={handleApplyPwdPerItem} className="w-full py-2.5 bg-[#303030] text-white rounded-xl text-sm font-medium hover:bg-black transition-colors">
                                    Apply SC/PWD discount
                                </button>
                            </div>
                        )}

                        {/* ── Promo Code Tab ── */}
                        {activeTab === 'promo' && (
                            <div className="space-y-4">
                                <p className="text-xs text-gray-500">Enter a promo or discount code issued by management.</p>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Discount code</label>
                                    <div className="flex gap-2">
                                        <input type="text" value={promoInput} onChange={e => { setPromoInput(e.target.value); setPromoResult(null) }}
                                            onKeyDown={e => e.key === 'Enter' && handleCheckPromo()}
                                            placeholder="e.g. KAPERK10"
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A687] uppercase" />
                                        <button onClick={handleCheckPromo} disabled={!promoInput.trim()}
                                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                                            Check
                                        </button>
                                    </div>
                                </div>
                                {promoResult && (
                                    promoResult.valid ? (
                                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2">
                                            <div className="flex items-center gap-2">
                                                <span className="bg-green-600 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full">{promoResult.code}</span>
                                                <span className="text-xs text-green-700">{promoResult.label} — valid</span>
                                            </div>
                                            <div className="flex justify-between text-xs text-green-700"><span>Discount</span><span className="font-medium">-₱{(subtotal * promoResult.rate).toFixed(2)}</span></div>
                                            <div className="flex justify-between text-sm border-t border-green-200 pt-2 font-semibold"><span className="text-green-800">New total</span><span className="text-green-600">₱{(subtotal - subtotal * promoResult.rate).toFixed(2)}</span></div>
                                        </div>
                                    ) : (
                                        <p className="text-xs text-red-500">Invalid or expired discount code.</p>
                                    )
                                )}
                                {promoResult?.valid && (
                                    <button onClick={handleApplyPromo} className="w-full py-2.5 bg-[#303030] text-white rounded-xl text-sm font-medium hover:bg-black transition-colors">
                                        Apply promo code
                                    </button>
                                )}
                            </div>
                        )}

                        {/* ── Loyalty Points Tab ── */}
                        {activeTab === 'loyalty' && (
                            <div className="space-y-4">
                                <p className="text-xs text-gray-500">
                                    Look up a customer to earn points on this purchase. ₱20 spent = 1 point.
                                </p>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Email or Membership Number</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={loyaltyQuery}
                                            onChange={e => { setLoyaltyQuery(e.target.value); setLoyaltyResult(null); setLoyaltyError(''); setEmailCheckDone(false) }}
                                            onKeyDown={e => e.key === 'Enter' && handleCheckEmail()}
                                            placeholder="juandelacruz@email.com"
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A687]"
                                        />
                                        <button
                                            onClick={handleCheckEmail}
                                            disabled={emailCheckLoading || !loyaltyQuery.trim()}
                                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                                        >
                                            {emailCheckLoading ? '...' : 'Find'}
                                        </button>
                                    </div>
                                    {loyaltyError && <p className="text-xs text-red-500 mt-1">{loyaltyError}</p>}

                                    {emailCheckDone && !loyaltyResult && !loyaltyError && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
                                            <p className="text-xs text-blue-700">
                                                No cached record for <span className="font-medium">{loyaltyQuery.trim()}</span> as of your last sync — this may be outdated.
                                                Enrolling now queues points under this email; if an account already exists, it'll be linked automatically once you're back online.
                                            </p>
                                            <button onClick={handleQuickSignup} disabled={signupLoading} className="w-full py-2 bg-[#303030] text-white rounded-lg text-sm font-medium disabled:opacity-50">
                                                {signupLoading ? 'Creating...' : 'Enroll & Link Order'}
                                            </button>
                                        </div>
                                    )}

                                </div>

                                {loyaltyResult && (
                                    <>
                                        <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-semibold flex-shrink-0">
                                                {loyaltyResult.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-800 truncate">{loyaltyResult.name}</p>
                                                <p className="text-xs text-gray-400">{loyaltyResult.membershipNumber}</p>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <p className="text-sm font-semibold text-amber-600">{loyaltyResult.pointsBalance.toLocaleString()} pts</p>
                                                <p className="text-xs text-gray-400">current balance</p>
                                            </div>
                                        </div>

                                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                                            <p className="text-xs text-amber-700">
                                                This customer will earn <span className="font-semibold">{Math.floor(total / 20)} pt{Math.floor(total / 20) !== 1 ? 's' : ''}</span> from this ₱{total.toFixed(2)} purchase.
                                            </p>
                                        </div>

                                        <button
                                            onClick={() => {
                                                setLoyaltyCustomer({
                                                    loyaltyId: loyaltyResult.loyaltyId,
                                                    customerId: loyaltyResult.customerId,
                                                    name: loyaltyResult.name,
                                                    membershipNumber: loyaltyResult.membershipNumber,
                                                })
                                                setShowDiscountModal(false)
                                            }}
                                            className="w-full py-2.5 bg-[#303030] text-white rounded-xl text-sm font-medium hover:bg-black transition-colors"
                                        >
                                            Link to this order
                                        </button>
                                    </>
                                )}
                            </div>
                        )}

                        {activeTab === 'vouchers' && (
                            <div className="space-y-3">
                                {!loyaltyCustomer ? (
                                    <p className="text-xs text-gray-500">Link a loyalty customer on the "Loyalty points" tab first — vouchers are checked against their account.</p>
                                ) : voucherLoading ? (
                                    <p className="text-xs text-gray-400 text-center py-4">Checking eligible vouchers...</p>
                                ) : voucherError ? (
                                    <p className="text-xs text-red-500">{voucherError}</p>
                                ) : voucherList.length === 0 ? (
                                    <p className="text-xs text-gray-400 text-center py-4">No active campaign vouchers for this branch right now.</p>
                                ) : (
                                    voucherList.map(v => {
                                        const usable = isVoucherUsable(v)
                                        const previewAmount = usable ? getVoucherDiscountPreview(v) : 0
                                        const isRecommended = usable && v.rewardId === bestVoucherRewardId
                                        const meetsMinimumSpend = subtotal >= (Number(v.minimumSpend) || 0)
                                        return (
                                            <div key={v.rewardId}
                                                onMouseDown={() => startLongPress(v)}
                                                onMouseUp={cancelLongPress}
                                                onMouseLeave={cancelLongPress}
                                                onTouchStart={() => startLongPress(v)}
                                                onTouchEnd={cancelLongPress}
                                                className={`p-3 rounded-xl border-2 select-none ${isRecommended ? 'border-green-400 bg-green-50' : usable ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <p className="text-sm font-medium text-gray-800">{v.rewardName}</p>
                                                            {isRecommended && (
                                                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-600 text-white">Recommended</span>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-gray-400">{v.campaignName}</p>
                                                        {usable && previewAmount > 0 && (
                                                            <p className="text-xs text-green-600 font-semibold mt-1">Save ₱{previewAmount.toFixed(2)}</p>
                                                        )}
                                                        {v.description && <p className="text-xs text-gray-500 mt-1">{v.description}</p>}
                                                        {!v.eligible && <p className="text-xs text-red-500 mt-1">{v.ineligibleReason}</p>}
                                                        {v.eligible && !meetsMinimumSpend && (
                                                            <p className="text-xs text-red-500 mt-1">Min. spend ₱{Number(v.minimumSpend).toFixed(2)}</p>
                                                        )}
                                                        {v.eligible && meetsMinimumSpend && previewAmount === 0 && v.rewardValueType !== 'bonus_points' && (
                                                            <p className="text-xs text-red-500 mt-1">Not applicable to items in this cart</p>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => handleApplyVoucher(v)}
                                                        disabled={!usable}
                                                        className="text-xs px-3 py-1.5 rounded-lg bg-[#303030] text-white disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                                                    >
                                                        Apply
                                                    </button>
                                                </div>
                                                <p className="text-[10px] text-gray-300 mt-1">Hold to view details</p>
                                            </div>
                                        )
                                    })
                                )}
                            </div>
                        )}

                    </div>
                </div>
            </div>

            {viewingVoucher && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setViewingVoucher(null)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <p className="text-lg font-bold text-[#8B4513]">
                                    {viewingVoucher.rewardValueType === 'percentage'
                                        ? `${viewingVoucher.rewardValue}% OFF`
                                        : viewingVoucher.rewardValueType === 'bonus_points'
                                            ? 'Bonus Points'
                                            : `₱${viewingVoucher.rewardValue} OFF`}
                                </p>
                                <p className="text-sm font-semibold text-gray-800 mt-1">{viewingVoucher.rewardName}</p>
                                <p className="text-xs text-gray-400">{viewingVoucher.campaignName}</p>
                            </div>
                            <button onClick={() => setViewingVoucher(null)} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
                        </div>
                        {viewingVoucher.description && (
                            <p className="text-xs text-gray-500 mb-3">{viewingVoucher.description}</p>
                        )}
                        <div className="bg-gray-50 rounded-xl p-3 space-y-1 text-xs text-gray-600">
                            {viewingVoucher.maxDiscountAmount != null && (
                                <p>Max discount: ₱{Number(viewingVoucher.maxDiscountAmount).toFixed(2)}</p>
                            )}
                            {Number(viewingVoucher.minimumSpend) > 0 && (
                                <p>Min. spend: ₱{Number(viewingVoucher.minimumSpend).toFixed(2)}</p>
                            )}
                            <p>
                                Applies to: {viewingVoucher.applicationScope === 'category'
                                    ? viewingVoucher.appliesToCategory || 'Category'
                                    : viewingVoucher.applicationScope === 'specific_items'
                                        ? 'Selected items'
                                        : 'All items'}
                            </p>
                            {isVoucherUsable(viewingVoucher) && (
                                <p className="text-green-600 font-semibold">
                                    Estimated savings: ₱{getVoucherDiscountPreview(viewingVoucher).toFixed(2)}
                                </p>
                            )}
                        </div>
                        <button
                            onClick={() => { handleApplyVoucher(viewingVoucher); setViewingVoucher(null) }}
                            disabled={!isVoucherUsable(viewingVoucher)}
                            className="w-full mt-4 py-2.5 bg-[#303030] text-white rounded-xl text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Apply this voucher
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}

const CASH_DENOMINATIONS = [1000, 500, 200, 100, 50, 20, 10, 5, 1]

function DenominationCounter({ counts, setCounts }) {
    const total = CASH_DENOMINATIONS.reduce((sum, d) => sum + d * (parseInt(counts[d], 10) || 0), 0)
    return (
        <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
                {CASH_DENOMINATIONS.map(d => (
                    <div key={d} className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-500 w-12 flex-shrink-0">₱{d}</span>
                        <input
                            type="number"
                            min="0"
                            max="9999"
                            maxLength={4}
                            value={counts[d] || ''}
                            onChange={e => {
                                const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 4) // cap at 4 digits = max 9999 bills/coins
                                setCounts(prev => ({ ...prev, [d]: digitsOnly }))
                            }}
                            placeholder="0"
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A687]"
                        />
                    </div>
                ))}
            </div>
            <div className="flex justify-between text-sm font-semibold bg-gray-50 rounded-lg px-3 py-2">
                <span className="text-gray-600">Counted total</span>
                <span className="text-gray-800">₱{total.toFixed(2)}</span>
            </div>
        </div>
    )
}

function CashDrawerModal({ session, transactions, staffBranchId, baristaId, baristaName, onClose, onSessionChange, isOnline }) {
    const [activeTab, setActiveTab] = useState(session ? 'shift' : 'open')
    const [openingCounts, setOpeningCounts] = useState({})
    const [closingCounts, setClosingCounts] = useState({})
    const [notes, setNotes] = useState('')
    const [closingNotes, setClosingNotes] = useState('')
    const [payType, setPayType] = useState('pay_out')
    const [payAmount, setPayAmount] = useState('')
    const [payReason, setPayReason] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const sumCounts = (counts) => CASH_DENOMINATIONS.reduce((sum, d) => sum + d * (parseInt(counts[d], 10) || 0), 0)

    const payIns = transactions.filter(t => t.type === 'pay_in').reduce((s, t) => s + Number(t.amount), 0)
    const payOuts = transactions.filter(t => t.type === 'pay_out').reduce((s, t) => s + Number(t.amount), 0)
    const expectedInDrawer = session ? Number(session.opening_amount) + payIns - payOuts : 0
    const closingTotal = sumCounts(closingCounts)
    const variance = closingTotal - expectedInDrawer

    const handleOpenDrawer = async () => {
        if (!baristaId) {
            Swal.fire({ icon: 'warning', title: 'No Barista Selected', text: 'Select a barista before opening the drawer.', confirmButtonColor: '#A0826D' })
            return
        }
        const openingAmount = sumCounts(openingCounts)

        const MAX_DRAWER_AMOUNT = 500000
        if (openingAmount > MAX_DRAWER_AMOUNT) {
            Swal.fire({ icon: 'warning', title: 'Amount Too High', text: `Starting cash of ₱${openingAmount.toLocaleString()} looks unusually high. Please double-check your counts.`, confirmButtonColor: '#A0826D' })
            return
        }
        if (openingAmount <= 0) {
            Swal.fire({ icon: 'warning', title: 'Enter a starting amount', text: 'Count at least one denomination.', confirmButtonColor: '#A0826D' })
            return
        }
        setSubmitting(true)
        try {
            if (!isOnline) {
                const session = await openCashDrawerOffline({
                    branchId: staffBranchId, baristaId, openingAmount,
                    openingDenominations: openingCounts, notes: notes.trim() || null,
                })
                onSessionChange(session, [])
                Swal.fire({ icon: 'info', title: 'Drawer Opened (Offline)', text: `Starting cash: ₱${openingAmount.toFixed(2)} — will sync once wifi returns.`, confirmButtonColor: '#A0826D', timer: 2200 })
                return
            }
            const { data, error } = await supabase
                .from('cash_drawer_sessions')
                .insert({
                    branch_id: staffBranchId,
                    barista_id: baristaId,
                    status: 'open',
                    opening_amount: openingAmount,
                    opening_denominations: openingCounts,
                    notes: notes.trim() || null,
                })
                .select()
                .single()
            if (error) throw error
            onSessionChange(data, [])
            Swal.fire({ icon: 'success', title: 'Drawer Opened', text: `Starting cash: ₱${openingAmount.toFixed(2)}`, confirmButtonColor: '#A0826D', timer: 1800 })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Open Drawer', text: err.message, confirmButtonColor: '#FF6F3C' })
        } finally {
            setSubmitting(false)
        }
    }

    const handleLogPayInOut = async () => {
        if (!baristaId) {
            Swal.fire({ icon: 'warning', title: 'No Barista Selected', text: 'Select a barista before logging cash movements.', confirmButtonColor: '#A0826D' })
            return
        }
        const amount = parseFloat(payAmount)
        const MAX_PAYOUT_AMOUNT = 100000
        if (isNaN(amount) || amount <= 0) {
            Swal.fire({ icon: 'warning', title: 'Invalid Amount', text: 'Enter a valid amount.', confirmButtonColor: '#A0826D' })
            return
        }
        if (amount > MAX_PAYOUT_AMOUNT) {
            Swal.fire({ icon: 'warning', title: 'Amount Too High', text: `Pay-in/out of ₱${amount.toLocaleString()} exceeds the ₱${MAX_PAYOUT_AMOUNT.toLocaleString()} limit. Contact your admin for larger cash movements.`, confirmButtonColor: '#A0826D' })
            return
        }
        if (payType === 'pay_out' && amount > expectedInDrawer) {
            Swal.fire({ icon: 'warning', title: 'Insufficient Drawer Cash', text: `Only ₱${expectedInDrawer.toFixed(2)} is expected in the drawer — cannot pay out more than that.`, confirmButtonColor: '#A0826D' })
            return
        }
        if (!payReason.trim()) {
            Swal.fire({ icon: 'warning', title: 'Reason Required', text: 'Please note why this cash movement happened.', confirmButtonColor: '#A0826D' })
            return
        }
        if (payReason.trim().length > 200) {
            Swal.fire({ icon: 'warning', title: 'Reason Too Long', text: 'Keep the reason under 200 characters.', confirmButtonColor: '#A0826D' })
            return
        }
        setSubmitting(true)
        try {
            if (!isOnline) {
                const tx = await logCashDrawerTransactionOffline({
                    sessionId: session.session_id, branchId: staffBranchId, baristaId,
                    type: payType, amount, reason: payReason.trim(),
                })
                onSessionChange(session, [tx, ...transactions])
                setPayAmount(''); setPayReason('')
                Swal.fire({ icon: 'success', title: payType === 'pay_in' ? 'Pay-In Logged (Offline)' : 'Pay-Out Logged (Offline)', confirmButtonColor: '#A0826D', timer: 1500 })
                return
            }
            const { data, error } = await supabase
                .from('cash_drawer_transactions')
                .insert({
                    session_id: session.session_id,
                    branch_id: staffBranchId,
                    barista_id: baristaId,
                    type: payType,
                    amount,
                    reason: payReason.trim(),
                })
                .select()
                .single()
            if (error) throw error
            onSessionChange(session, [data, ...transactions])
            setPayAmount(''); setPayReason('')
            Swal.fire({ icon: 'success', title: payType === 'pay_in' ? 'Pay-In Logged' : 'Pay-Out Logged', confirmButtonColor: '#A0826D', timer: 1500 })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Log', text: err.message, confirmButtonColor: '#FF6F3C' })
        } finally {
            setSubmitting(false)
        }
    }

    const handleCloseDrawer = async () => {
        if (closingTotal <= 0) {
            Swal.fire({ icon: 'warning', title: 'Enter the counted cash', text: 'Count at least one denomination.', confirmButtonColor: '#A0826D' })
            return
        }
        if (variance !== 0 && !closingNotes.trim()) {
            Swal.fire({ icon: 'warning', title: 'Note Required', text: `There's a variance of ₱${variance.toFixed(2)}. Please add a note explaining why before closing.`, confirmButtonColor: '#A0826D' })
            return
        }
        const confirm = await Swal.fire({
            icon: variance === 0 ? 'success' : 'warning',
            title: 'Confirm Drawer Close',
            html: `<div style="text-align:left; font-size:14px; line-height:1.8">
                <div>Expected: ₱${expectedInDrawer.toFixed(2)}</div>
                <div>Counted: ₱${closingTotal.toFixed(2)}</div>
                <div style="font-weight:600; color:${variance === 0 ? '#16a34a' : '#dc2626'}">Variance: ${variance >= 0 ? '+' : ''}₱${variance.toFixed(2)}</div>
            </div>`,
            showCancelButton: true, confirmButtonText: 'Close Drawer', confirmButtonColor: '#303030',
        })
        if (!confirm.isConfirmed) return
                setSubmitting(true)
        try {
            if (!isOnline) {
                await closeCashDrawerOffline({
                    sessionId: session.session_id, branchId: staffBranchId, baristaId,
                    closingAmount: closingTotal, closingDenominations: closingCounts,
                    closingNotes: closingNotes.trim() || null, expectedAmount: expectedInDrawer, variance,
                })
                onSessionChange(null, [])
                onClose()
                Swal.fire({ icon: 'info', title: 'Drawer Closed (Offline)', text: `Variance: ${variance >= 0 ? '+' : ''}₱${variance.toFixed(2)} — will sync once wifi returns.`, confirmButtonColor: '#A0826D' })
                return
            }
            const { error } = await supabase
                .from('cash_drawer_sessions')
                .update({
                    status: 'closed',
                    closed_by_barista_id: baristaId,
                    closing_amount: closingTotal,
                    closing_denominations: closingCounts,
                    closing_notes: closingNotes.trim() || null,
                    expected_amount: expectedInDrawer,
                    variance,
                    closed_at: new Date().toISOString(),
                })
                .eq('session_id', session.session_id)
            if (error) throw error
            onSessionChange(null, [])
            onClose()
            Swal.fire({ icon: 'success', title: 'Drawer Closed', text: `Variance: ${variance >= 0 ? '+' : ''}₱${variance.toFixed(2)}`, confirmButtonColor: '#A0826D' })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Close Drawer', text: err.message, confirmButtonColor: '#FF6F3C' })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div>
                        <h2 className="text-base font-semibold text-gray-800">Cash Drawer</h2>
                        {session && <p className="text-xs text-gray-400 mt-0.5">Opened {new Date(session.opened_at).toLocaleTimeString()}</p>}
                        {baristaName && <p className="text-xs text-gray-400 mt-0.5">Barista: {baristaName}</p>}
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-sm">✕</button>
                </div>

                {session && (
                    <div className="flex border-b border-gray-100">
                        {[{ key: 'shift', label: 'Pay In/Out' }, { key: 'close', label: 'Close Drawer' }].map(tab => (
                            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                className={`flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-[#303030] text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}

                <div className="overflow-y-auto flex-1 p-6">
                    {!session && (
                        <div className="space-y-4">
                            <p className="text-xs text-gray-500">Count the starting cash in the drawer before your shift.</p>
                            <DenominationCounter counts={openingCounts} setCounts={setOpeningCounts} />
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Notes (optional)</label>
                                <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A687]" />
                            </div>
                            <button onClick={handleOpenDrawer} disabled={submitting}
                                className="w-full py-2.5 bg-[#303030] text-white rounded-xl text-sm font-medium hover:bg-black transition-colors disabled:opacity-50">
                                {submitting ? 'Opening...' : 'Open Drawer'}
                            </button>
                        </div>
                    )}

                    {session && activeTab === 'shift' && (
                        <div className="space-y-4">
                            <div className="bg-gray-50 rounded-xl p-4 space-y-1.5 text-xs">
                                <div className="flex justify-between"><span className="text-gray-500">Opening amount</span><span className="font-medium text-gray-700">₱{Number(session.opening_amount).toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Pay-ins</span><span className="font-medium text-green-600">+₱{payIns.toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Pay-outs</span><span className="font-medium text-red-500">-₱{payOuts.toFixed(2)}</span></div>
                                <div className="flex justify-between border-t border-gray-200 pt-1.5 font-semibold"><span className="text-gray-700">Expected in drawer</span><span className="text-gray-800">₱{expectedInDrawer.toFixed(2)}</span></div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-2">Type</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {[{ v: 'pay_out', l: 'Pay Out' }, { v: 'pay_in', l: 'Pay In' }].map(opt => (
                                        <button key={opt.v} onClick={() => setPayType(opt.v)}
                                            className={`py-2 rounded-lg border text-xs font-medium transition-colors ${payType === opt.v ? 'bg-[#303030] border-[#303030] text-white' : 'bg-white border-gray-300 text-gray-700'}`}>
                                            {opt.l}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Amount</label>
                                <input type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)}
                                    placeholder="0.00"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A687]" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Reason</label>
                                <input type="text" value={payReason} onChange={e => setPayReason(e.target.value)}
                                    placeholder="e.g. Change fund top-up, supplier payment..."
                                    maxLength={200}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A687]" />
                            </div>
                            <button onClick={handleLogPayInOut} disabled={submitting}
                                className="w-full py-2.5 bg-[#303030] text-white rounded-xl text-sm font-medium hover:bg-black transition-colors disabled:opacity-50">
                                {submitting ? 'Logging...' : `Log ${payType === 'pay_in' ? 'Pay-In' : 'Pay-Out'}`}
                            </button>

                            {transactions.length > 0 && (
                                <div className="space-y-1.5 pt-2 border-t border-gray-100">
                                    <p className="text-xs font-medium text-gray-500">Recent activity</p>
                                    {transactions.slice(0, 6).map(t => (
                                        <div key={t.transaction_id} className="flex justify-between text-xs">
                                            <span className="text-gray-500">{t.reason}</span>
                                            <span className={t.type === 'pay_in' ? 'text-green-600' : 'text-red-500'}>
                                                {t.type === 'pay_in' ? '+' : '-'}₱{Number(t.amount).toFixed(2)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {session && activeTab === 'close' && (
                        <div className="space-y-4">
                            <p className="text-xs text-gray-500">Count the cash currently in the drawer to close out this session.</p>
                            <DenominationCounter counts={closingCounts} setCounts={setClosingCounts} />
                            <div className="bg-gray-50 rounded-xl p-4 space-y-1.5 text-xs">
                                <div className="flex justify-between"><span className="text-gray-500">Expected in drawer</span><span className="font-medium text-gray-700">₱{expectedInDrawer.toFixed(2)}</span></div>
                                <div className="flex justify-between border-t border-gray-200 pt-1.5 font-semibold">
                                    <span className="text-gray-700">Variance</span>
                                    <span className={variance === 0 ? 'text-green-600' : 'text-red-500'}>{variance >= 0 ? '+' : ''}₱{variance.toFixed(2)}</span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                    Notes {variance !== 0 && <span className="text-red-500">(explain the variance)</span>}
                                </label>
                                <textarea
                                    value={closingNotes}
                                    onChange={e => setClosingNotes(e.target.value)}
                                    placeholder="e.g. Gave wrong change on order #123, drawer was short at handoff..."
                                    rows={3}
                                    maxLength={300}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A687] resize-none"
                                />
                                <p className="text-xs text-gray-400 mt-1 text-right">{closingNotes.length}/300</p>
                            </div>

                            <button onClick={handleCloseDrawer} disabled={submitting}
                                className="w-full py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50">
                                {submitting ? 'Closing...' : 'Close Drawer'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default function POSLayout({ onBack, btChar, btStatus, btPrinterName, printerMode, isVisible = true }) {
    const [staffBranchId, setStaffBranchId] = useState(null)
    const [loggedInStaffId, setLoggedInStaffId] = useState(null)
    const { selectedBarista, branchBaristas } = useBarista()
    const [cashDrawerSession, setCashDrawerSession] = useState(null)
    const [cashDrawerTransactions, setCashDrawerTransactions] = useState([])
    const [loyaltyCustomer, setLoyaltyCustomer] = useState(null)
    const [loyaltyQuery, setLoyaltyQuery] = useState('')
    const [loyaltyResult, setLoyaltyResult] = useState(null)
    const [emailCheckDone, setEmailCheckDone] = useState(false)

    // TWO-LEVEL CATEGORY SYSTEM
    const [activeTab, setActiveTab] = useState(null)
    const [activeClassification, setActiveClassification] = useState(null)
    const [showClassificationModal, setShowClassificationModal] = useState(false)
    const [availableClassifications, setAvailableClassifications] = useState([])

    const [showUserMenu, setShowUserMenu] = useState(false)
    const [userData, setUserData] = useState(null)
    const [showProfile, setShowProfile] = useState(false)
    const [menuItems, setMenuItems] = useState([])
    const [allBranchItems, setAllBranchItems] = useState([])
    const [menuSearchQuery, setMenuSearchQuery] = useState('')
    const [loadingMenu, setLoadingMenu] = useState(false)

    const [cartItems, setCartItems] = useState([])
    const [editingCartKey, setEditingCartKey] = useState(null)
    const [expandedCartKeys, setExpandedCartKeys] = useState(new Set())

    const [selectedItem, setSelectedItem] = useState(null)
    const [variants, setVariants] = useState([])
    const [modifiers, setModifiers] = useState([])
    const [selectedVariants, setSelectedVariants] = useState({})
    const [selectedModifiers, setSelectedModifiers] = useState([])
    const [loadingCustomization, setLoadingCustomization] = useState(false)

    const [showPaymentModal, setShowPaymentModal] = useState(false)
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash')
    const [amountReceived, setAmountReceived] = useState('')
    const [isProcessingPayment, setIsProcessingPayment] = useState(false)
    const [isSplitPayment, setIsSplitPayment] = useState(false)
    const [splitPayments, setSplitPayments] = useState([])
    const [selectedFulfillmentType, setSelectedFulfillmentType] = useState('dine_in')
    const [paymentReferenceNumber, setPaymentReferenceNumber] = useState('')

    const [showPinModal, setShowPinModal] = useState(false)
    const [enteredPin, setEnteredPin] = useState('')
    const [pendingVoidItem, setPendingVoidItem] = useState(null)

    const [voidSelectionMode, setVoidSelectionMode] = useState(false)
    const [selectedVoidKeys, setSelectedVoidKeys] = useState(new Set())


    // Printer
    const [showReceiptModal, setShowReceiptModal] = useState(false)
    const [completedOrder, setCompletedOrder] = useState(null)
    const [selectedStickerItems, setSelectedStickerItems] = useState(new Set())
    const [receiptView, setReceiptView] = useState('receipt')

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'

    //guard
    const isSubmittingRef = React.useRef(false)

    //offline
    const isOnline = useNetworkStatus()
    const pendingSyncCount = usePendingSyncCount()

    useEffect(() => {
        const handleSyncError = (e) => {
            Swal.fire({
                icon: 'error',
                title: `Sync Failed: ${e.detail.type}`,
                text: e.detail.message,
                confirmButtonColor: '#A0826D',
            })
        }
        window.addEventListener('kaperk:sync-error', handleSyncError)
        return (

        ) => window.removeEventListener('kaperk:sync-error', handleSyncError)
    }, [])

    useEffect(() => {
        const handleLoyaltySynced = (e) => {
            setLoyaltyCustomer(prev => {
                if (!prev || prev.loyaltyId !== e.detail.localLoyaltyKey) return prev
                return { ...prev, loyaltyId: e.detail.realLoyaltyId, customerId: e.detail.realUserId }
            })
        }
        window.addEventListener('kaperk:loyalty-synced', handleLoyaltySynced)
        return () => window.removeEventListener('kaperk:loyalty-synced', handleLoyaltySynced)
    }, [])

    useEffect(() => {
        fetchUserData()
    }, [])

    useEffect(() => {
        if (!staffBranchId) return
        return () => {
            // Best-effort clear on unmount (tab close, navigating away) so the
            // badge doesn't show a stale barista once nobody's actually at POS.
            supabase
                .from('branches')
                .update({ active_pos_barista_id: null })
                .eq('branch_id', staffBranchId)
                .then(() => {})
        }
    }, [staffBranchId])

    // ── Cash Drawer Auto-Handoff ──────────────────────────────────────────
    // Only rolls the drawer to whoever is currently selected if they're actually
    // standing at the POS screen (isVisible). A barista logging in elsewhere just
    // to clock in/out never triggers this, so the active cashier's session stays
    // untouched.
    const handoffInFlightRef = React.useRef(false)

    useEffect(() => {
        if (!isVisible) return
        if (!staffBranchId || !selectedBarista?.barista_id) return
        if (!cashDrawerSession) return
        if (cashDrawerSession.barista_id === selectedBarista.barista_id) return
        if (handoffInFlightRef.current) return

        handoffInFlightRef.current = true
        const outgoing = branchBaristas.find(b => b.barista_id === cashDrawerSession.barista_id) || null

        autoRotateCashDrawer({
            branchId: staffBranchId,
            outgoingBarista: outgoing,
            incomingBarista: selectedBarista,
        })
            .then(() => fetchOpenCashDrawerSession())
            .finally(() => { handoffInFlightRef.current = false })
    }, [isVisible, staffBranchId, selectedBarista?.barista_id, cashDrawerSession])

    // ── POS Heartbeat ──────────────────────────────────────────────────────
    // Lets the mobile app know this branch's POS terminal is actively staffed.
    // Only fires while the POS screen is actually visible (isVisible) — a
    // selectedBarista in context does NOT mean someone is standing at the
    // terminal, so heartbeats must not run just because a barista is selected
    // elsewhere in the app.
    useEffect(() => {
        if (!staffBranchId || !isOnline) return

        if (!isVisible) {
            // Screen was hidden (not unmounted) — clear immediately instead of
            // waiting for unmount, which may never happen if POSLayout stays
            // mounted behind another screen.
            supabase
                .from('branches')
                .update({ active_pos_barista_id: null })
                .eq('branch_id', staffBranchId)
                .then(() => {})
            return
        }

        const sendHeartbeat = async () => {
            try {
                await supabase
                    .from('branches')
                    .update({
                        pos_last_heartbeat: new Date().toISOString(),
                        active_pos_barista_id: selectedBarista?.barista_id ?? null,
                    })
                    .eq('branch_id', staffBranchId)
            } catch (err) {
                console.error('POS heartbeat failed:', err)
            }
            refreshOrdersCacheForReceiptNumbers(staffBranchId).catch(err =>
                console.error('Receipt number cache refresh failed:', err)
            )
        }

        sendHeartbeat()
        const interval = setInterval(sendHeartbeat, 20_000)
        return () => clearInterval(interval)
    }, [staffBranchId, isOnline, isVisible, selectedBarista?.barista_id])

    // useEffect(() => {
    //     if (staffBranchId) {
    //         fetchAllBranchItems(staffBranchId)
    //     }
    // }, [staffBranchId])

    useEffect(() => {
        if (staffBranchId && activeTab && activeClassification) {
            fetchMenuItems(activeTab, activeClassification, staffBranchId)
        } else {
            setMenuItems([])
        }
    }, [activeTab, activeClassification, staffBranchId])

    useEffect(() => {
        if (!isOnline || !staffBranchId || allBranchItems.length === 0) return
        refreshOfflineCaches(staffBranchId, allBranchItems).catch(err =>
            console.error('Offline cache refresh failed:', err)
        )
        getStaffContext(true).catch(err =>
            console.error('Staff context cache refresh failed:', err)
        )
        refreshOrdersCacheForReceiptNumbers(staffBranchId).catch(err =>
            console.error('Receipt number cache refresh failed:', err)
        )
    }, [isOnline, staffBranchId, allBranchItems.length])

    const [showDiscountModal, setShowDiscountModal] = useState(false)
    const [appliedDiscount, setAppliedDiscount] = useState(null)

    const [showCashDrawerModal, setShowCashDrawerModal] = useState(false)

    const [remakeRequests, setRemakeRequests] = useState([])
    const [showRemakeListModal, setShowRemakeListModal] = useState(false)
    const [activeRemakeRequest, setActiveRemakeRequest] = useState(null)
    const [showRemakeItemSelectModal, setShowRemakeItemSelectModal] = useState(false)
    const [remakeCandidateItems, setRemakeCandidateItems] = useState([])
    const [selectedRemakeCandidateIds, setSelectedRemakeCandidateIds] = useState(new Set())
    const [pendingRemakeRequest, setPendingRemakeRequest] = useState(null)

    const isOfflineRemake = (r) =>
    !isOnline || String(r?.remake_request_id || '').startsWith('local_')

    const buildRemakeCandidates = (items, request) => {
        const selectedIds = new Set((request.remake_items || []).map(i => i.order_item_id))
        const source = selectedIds.size > 0 ? items.filter(i => selectedIds.has(i.order_item_id)) : items

        return source.map(item => {
            const variantObjects = (item.order_item_variants || []).map(v => ({
                variant_id: v.variant_id ?? v.item_variants?.variant_id,
                variant_name: v.item_variants?.variant_name,
                category: v.item_variants?.category,
                price: v.variant_price || 0,
            }))
            const modifierObjects = (item.order_item_modifiers || []).map(m => ({
                modifier_id: m.modifier_id ?? m.item_modifiers?.modifier_id,
                modifier_name: m.item_modifiers?.modifier_name,
                price: m.modifier_price || 0,
            }))
            return {
                order_item_id: item.order_item_id,
                cartKey: `remake_${item.order_item_id}`,
                item_id: item.item_id,
                name: item.menu_items?.name || item.name || 'Unknown Item',
                unitPrice: item.item_price,
                qty: item.quantity,
                variants: variantObjects.map(v => v.variant_name),
                modifiers: modifierObjects.map(m => m.modifier_name),
                variantObjects, modifierObjects,
                line_status: 'active',
            }
        })
    }

    // Release a claim online or offline depending on the request
    const releaseRemake = async (request) => {
        if (!request) return
        if (isOfflineRemake(request)) {
            await releaseRemakeRequestOffline(request.remake_request_id)
        } else {
            await supabase
                .from('remake_requests')
                .update({ status: 'pending', claimed_by: null })
                .eq('remake_request_id', request.remake_request_id)
                .eq('status', 'in_progress')
        }
    }

    const fetchRemakeRequests = async () => {
        if (!staffBranchId) return

        const readCache = async () => {
            const cached = await getActiveRemakeRequestsOffline(staffBranchId)
            setRemakeRequests(cached.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)))
        }

        if (!isOnline) { await readCache(); return }

        try {
            const { data, error } = await supabase
                .from('remake_requests')
                .select('remake_request_id, order_id, reference_number, branch_id, reason, responsible_staff, remake_items, submitted_by, submitted_by_barista, claimed_by, status, created_at')
                .eq('branch_id', staffBranchId)
                .in('status', ['pending', 'in_progress'])
                .order('created_at', { ascending: true })
            if (error) throw error
            const serverRows = data || []

            // Refresh the offline cache, but keep requests that were created offline
            // and haven't synced yet (they only exist locally).
            await offlineDB.transaction('rw', offlineDB.remakeRequestsCache, async () => {
                const existing = await offlineDB.remakeRequestsCache.where('branch_id').equals(staffBranchId).toArray()
                const staleIds = existing
                    .filter(r => !String(r.remake_request_id).startsWith('local_'))
                    .map(r => r.remake_request_id)
                await offlineDB.remakeRequestsCache.bulkDelete(staleIds)
                await offlineDB.remakeRequestsCache.bulkPut(serverRows.map(r => ({ ...r, branch_id: staffBranchId })))
            })

            const localCandidates = (await getActiveRemakeRequestsOffline(staffBranchId))
                .filter(r => String(r.remake_request_id).startsWith('local_'))
            const localPending = []
            for (const r of localCandidates) {
                const mapped = await offlineDB.idMap.get(r.remake_request_id)
                if (mapped) await offlineDB.remakeRequestsCache.delete(r.remake_request_id) // already synced
                else localPending.push(r)
            }

            setRemakeRequests([...serverRows, ...localPending])
        } catch (err) {
            console.error('Error fetching remake requests:', err)
            await readCache() // flaky connection -> fall back to cache
        }
    }

    useEffect(() => {
        if (!staffBranchId) return
        fetchRemakeRequests()

        const handler = () => fetchRemakeRequests()
        window.addEventListener('kaperk:remake-request-changed', handler)
        window.addEventListener('kaperk:sync-complete', handler)

        let channel = null
        if (isOnline) {
            channel = supabase
                .channel(`remake-requests-${staffBranchId}`)
                .on('postgres_changes', { event: '*', schema: 'public', table: 'remake_requests', filter: `branch_id=eq.${staffBranchId}` }, handler)
                .subscribe()
        }

        return () => {
            window.removeEventListener('kaperk:remake-request-changed', handler)
            window.removeEventListener('kaperk:sync-complete', handler)
            if (channel) supabase.removeChannel(channel)
        }
    }, [staffBranchId, isOnline])

    useEffect(() => {
        if (!staffBranchId) return
        fetchOpenCashDrawerSession()
    }, [staffBranchId, isOnline])

    const handleOpenRemakeProcessing = async (request) => {
        try {
            const currentClaimant = selectedBarista?.barista_id || loggedInStaffId

            if (isOfflineRemake(request)) {
                const myPossibleIds = selectedBarista?.barista_id
                    ? new Set([selectedBarista.barista_id]) : new Set([loggedInStaffId].filter(Boolean))
                const isOriginalSubmitter = selectedBarista?.barista_id
                    ? request.submitted_by_barista?.barista_id === selectedBarista.barista_id
                    : request.submitted_by === loggedInStaffId

                if (request.status === 'in_progress' && !myPossibleIds.has(request.claimed_by) && !isOriginalSubmitter) {
                    Swal.fire({ icon: 'info', title: 'Already Being Processed', confirmButtonColor: '#A0826D' })
                    fetchRemakeRequests(); return
                }

                const { conflict, request: claimed } = await claimRemakeRequestOffline(request.remake_request_id, currentClaimant)
                if (conflict) {
                    Swal.fire({ icon: 'info', title: 'Already Being Processed', confirmButtonColor: '#A0826D' })
                    fetchRemakeRequests(); return
                }

                const items = await offlineDB.orderItemsCache.where('order_id').equals(request.order_id).toArray()
                const activeItems = items.filter(i => !['voided', 'void_approved'].includes(i.line_status))
                const candidates = buildRemakeCandidates(activeItems, request)

                if (candidates.length === 0) {
                    await releaseRemakeRequestOffline(request.remake_request_id)
                    Swal.fire({ icon: 'warning', title: 'Order Items Not Cached', text: 'Reconnect once so this order\'s items can be cached, then try again.', confirmButtonColor: '#A0826D' })
                    return
                }

                setPendingRemakeRequest(claimed)
                setRemakeCandidateItems(candidates)
                setSelectedRemakeCandidateIds(new Set(candidates.map(c => c.order_item_id)))
                setShowRemakeListModal(false)
                setShowRemakeItemSelectModal(true)
                return
            }
            // Look at the row's live state before deciding what to do:
            // - pending           → claim it now
            // - in_progress + mine → this is me reopening my own claim, let it through
            // - in_progress + someone else's → blocked, full stop
            const { data: currentRequest, error: fetchError } = await supabase
                .from('remake_requests')
                .select('remake_request_id, status, claimed_by, submitted_by, submitted_by_barista')
                .eq('remake_request_id', request.remake_request_id)
                .maybeSingle()
            if (fetchError) throw fetchError

            // When a barista is selected, identity is scoped to that barista —
            // loggedInStaffId is the shared terminal login and must NOT count
            // as "me", or every barista on this terminal would match each other.
            const myPossibleIds = selectedBarista?.barista_id
                ? new Set([selectedBarista.barista_id])
                : new Set([loggedInStaffId].filter(Boolean))
            const isOriginalSubmitter = selectedBarista?.barista_id
                ? currentRequest?.submitted_by_barista?.barista_id === selectedBarista.barista_id
                : currentRequest?.submitted_by === loggedInStaffId

            if (currentRequest?.status === 'in_progress') {
                if (!myPossibleIds.has(currentRequest.claimed_by) && !isOriginalSubmitter) {
                    Swal.fire({ icon: 'info', title: 'Already Being Processed', text: 'Only the staff member who opened this remake request can continue it.', confirmButtonColor: '#A0826D' })
                    fetchRemakeRequests()
                    return
                }
                // Mine, or I'm the original submitter reopening it — reclaim to keep it in sync.
                if (!myPossibleIds.has(currentRequest.claimed_by)) {
                    const { error: reclaimError } = await supabase
                        .from('remake_requests')
                        .update({ claimed_by: currentClaimant })
                        .eq('remake_request_id', request.remake_request_id)
                    if (reclaimError) throw reclaimError
                }
            } else {
                const { data: claimResult, error: claimError } = await supabase
                    .from('remake_requests')
                    .update({ status: 'in_progress', claimed_by: currentClaimant })
                    .eq('remake_request_id', request.remake_request_id)
                    .eq('status', 'pending')
                    .select()
                    .maybeSingle()
                if (claimError) throw claimError
                if (!claimResult) {
                    Swal.fire({ icon: 'info', title: 'Already Being Processed', text: 'Another staff member has already opened this remake request.', confirmButtonColor: '#A0826D' })
                    fetchRemakeRequests()
                    return
                }
            }

            const { data: items, error: itemsError } = await supabase
                .from('order_items')
                .select(`*, menu_items ( name, item_price ), order_item_variants ( variant_id, variant_price, item_variants ( variant_id, variant_name, category ) ), order_item_modifiers ( modifier_id, modifier_price, item_modifiers ( modifier_id, modifier_name ) )`)
                .eq('order_id', request.order_id)
                .not('line_status', 'in', '("voided","void_approved")')
            if (itemsError) throw itemsError

            // Only remake the item(s) the barista actually flagged when submitting
            // the request — older requests with nothing recorded here fall back
            // to remaking every active item (previous behavior).
            const candidates = buildRemakeCandidates(items || [], request)

            // Let the barista pick which of the flagged item(s) to actually load
            // into the cart — a request may cover several items but only one
            // of them might actually be the wrong order.
            setPendingRemakeRequest({ ...request, status: 'in_progress', claimed_by: currentClaimant })
            setRemakeCandidateItems(candidates)
            setSelectedRemakeCandidateIds(new Set(candidates.map(c => c.order_item_id)))
            setShowRemakeListModal(false)
            setShowRemakeItemSelectModal(true)
        } catch (err) {
            console.error('Error opening remake request:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Open Remake', text: err.message || 'An error occurred.', confirmButtonColor: '#A0826D' })
        }
    }

    const handleToggleRemakeCandidate = (orderItemId) => {
        setSelectedRemakeCandidateIds(prev => {
            const next = new Set(prev)
            next.has(orderItemId) ? next.delete(orderItemId) : next.add(orderItemId)
            return next
        })
    }

    const handleToggleSelectAllRemakeCandidates = () => {
        setSelectedRemakeCandidateIds(prev =>
            prev.size === remakeCandidateItems.length
                ? new Set()
                : new Set(remakeCandidateItems.map(c => c.order_item_id))
        )
    }

    const handleCancelRemakeItemSelection = async () => {
        try { await releaseRemake(pendingRemakeRequest) }
        catch (err) { console.error('Failed to release remake request:', err) }
        setShowRemakeItemSelectModal(false)
        setPendingRemakeRequest(null)
        setRemakeCandidateItems([])
        setSelectedRemakeCandidateIds(new Set())
        fetchRemakeRequests()
    }

    const handleConfirmRemakeItemSelection = () => {
        if (selectedRemakeCandidateIds.size === 0) {
            Swal.fire({ icon: 'warning', title: 'No Items Selected', text: 'Please select at least one item to remake.', confirmButtonColor: '#A0826D' })
            return
        }

        const loadedCart = remakeCandidateItems
            .filter(c => selectedRemakeCandidateIds.has(c.order_item_id))
            .map(({ order_item_id, ...cartFields }) => cartFields)

        setActiveRemakeRequest(pendingRemakeRequest)
        setCartItems(loadedCart)
        setAppliedDiscount(null)
        setLoyaltyCustomer(null)

        setShowRemakeItemSelectModal(false)
        setPendingRemakeRequest(null)
        setRemakeCandidateItems([])
        setSelectedRemakeCandidateIds(new Set())

        Swal.fire({ icon: 'info', title: 'Remake Order Loaded', text: 'Review and correct the items, then proceed to checkout.', confirmButtonColor: '#A0826D', timer: 2200 })
    }

    const handleCancelRemakeMode = async () => {
        try { await releaseRemake(activeRemakeRequest) }
        catch (err) { console.error('Failed to release remake request:', err) }
        setActiveRemakeRequest(null)
        setCartItems([])
        setAppliedDiscount(null)
        fetchRemakeRequests()
    }

    // Tab 
    const tabs = [
        {
            name: 'Drink',
            icon: (
                <svg width="28" height="28" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 2L4 0H11L12 2H12.5C12.78 2 13 2.22 13 2.5V3.5C13 3.78 12.78 4 12.5 4H12L11 15H4L3 4H2.5C2.22 4 2 3.78 2 3.5V2.5C2 2.22 2.22 2 2.5 2H3ZM10.82 6L11 4H4L4.19 6H10.82ZM10.27 12H4.73L4.91 14H10.09L10.27 12Z" fill="currentColor" />
                </svg>
            )
        },
        {
            name: 'Dessert',
            icon: (
                <svg width="28" height="28" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.989 4.99109C9.40375 5.89678 9.16141 7.214 8.29994 7.77025C7.35434 8.38084 6.09281 8.10925 5.48225 7.16369C5.41799 7.06389 5.36252 6.95871 5.31647 6.84931L0 9.18206V14.0516H14.0605V9.78281C13.8232 9.60966 13.5988 9.31809 13.5164 8.88822C13.2493 8.7154 13.0535 8.45193 12.9651 8.14628C12.5154 8.04866 12.1464 7.72688 11.9918 7.29681C11.478 7.20184 11.0645 6.81484 10.9386 6.31163C10.5186 6.19306 10.2493 5.94131 10.1089 5.53694C9.74544 5.55428 9.34297 5.39413 8.989 4.99109ZM13.1447 12.9442H0.879625V12.195H13.1447V12.9442ZM13.1447 10.4476H0.879625V9.66731H13.1447V10.4476ZM8.08275 4.85481C7.93469 4.75919 7.80484 4.69331 7.64269 4.65425C7.5985 4.46044 7.55244 4.26909 7.52697 4.14316C7.35644 3.29988 6.97391 2.47866 6.21081 2.02925C5.85694 1.81906 5.38069 2.03972 5.32513 2.45153C5.27844 2.79759 5.50591 2.97413 5.777 3.11C6.33722 3.39084 6.66003 3.9265 6.87519 4.50769C6.89009 4.54791 6.90447 4.59716 6.92 4.65013C6.54681 4.73525 6.20519 4.96022 5.98112 5.30722C5.52572 6.01247 5.72828 6.95341 6.43353 7.40881C7.13884 7.86422 8.07975 7.66169 8.53516 6.95641C8.99056 6.25113 8.788 5.31019 8.08275 4.85478M7.71469 5.91275C7.50462 5.91275 7.33434 5.74244 7.33434 5.53244C7.33434 5.32234 7.50463 5.15206 7.71466 5.15206C7.92472 5.15206 8.095 5.32238 8.095 5.53241C8.095 5.74247 7.92472 5.91275 7.71469 5.91275ZM15.2477 9.73647V14.0515H14.4381V9.61547C14.1518 9.435 13.9615 9.11622 13.9615 8.75281L13.9616 8.75056C13.5645 8.63697 13.2667 8.28869 13.2276 7.86534C12.7307 7.85344 12.3137 7.48634 12.2378 7.00791C11.6943 7.00791 11.2422 6.58272 11.2121 6.04678C10.7817 6.00756 10.4289 5.70084 10.3208 5.29434C10.2789 5.29963 10.2367 5.30233 10.1944 5.30244C9.6315 5.30244 9.17516 4.84609 9.17516 4.28316C9.17516 3.72022 9.6315 3.26391 10.1944 3.26391C10.6666 3.26391 11.0637 3.58503 11.1794 4.02078C11.2213 4.01551 11.2635 4.01282 11.3057 4.01272C11.8492 4.01272 12.2932 4.43809 12.3232 4.97403C12.7887 5.01647 13.1719 5.37147 13.2443 5.82766C13.7752 5.82766 14.2194 6.23347 14.2672 6.75194C14.8186 6.76519 15.2616 7.216 15.2616 7.77059L15.2615 7.77303C15.6878 7.89497 16 8.28731 16 8.75281C16 9.22334 15.6811 9.61919 15.2477 9.7365" fill="currentColor" />
                </svg>
            )
        },
        {
            name: 'Meal',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 32 32">
                    <path fill="currentColor" d="M18 9.5A7.5 7.5 0 0 1 25.5 2h.5a1 1 0 0 1 1 1v12c0 .158.04.668.118 1.466c.074.77.175 1.743.283 2.78l.013.123c.27 2.598.586 5.646.586 6.631a4 4 0 1 1-8 0c0-.838.227-3.367.458-5.738c.159-1.635.324-3.24.431-4.262H20.5a2.5 2.5 0 0 1-2.5-2.5zm-3.026-6.727a1 1 0 0 0-1.035-.771c-.53.03-.949.467-.949 1.004v6a.995.995 0 0 1-1.99.03V3a1 1 0 1 0-2 0v6.065a.995.995 0 0 1-1.988-.06V3.007a1.007 1.007 0 0 0-1.986-.233C5.004 2.866 4 7.14 4 10a5.99 5.99 0 0 0 2.27 4.7c.496.394.73.823.73 1.182a1 1 0 0 1-.007.114c-.06.504-.307 2.594-.539 4.753C6.226 22.872 6 25.16 6 26a4 4 0 0 0 8 0c0-.839-.226-3.128-.454-5.251c-.232-2.159-.48-4.25-.54-4.753a1 1 0 0 1-.006-.114c0-.359.234-.788.73-1.182A5.99 5.99 0 0 0 16 10c0-2.862-1.007-7.144-1.026-7.227m-1.08-.768l.02-.001h-.008l-.008.001z" />
                </svg>
            )
        }
    ]

    const handleTabClick = async (tabName) => {
        if (!staffBranchId) return
        setMenuSearchQuery('')

        if (activeTab === tabName) {
            setActiveTab(null)
            setActiveClassification(null)
            setAvailableClassifications([])
            setMenuItems([])
            return
        }

        setActiveTab(tabName)
        setActiveClassification(null)
        setMenuItems([])

        // Offline: derive classifications from the already-cached branch items
        // instead of querying Supabase (which will hang, then fail, then wipe
        // availableClassifications).
        if (!isOnline) {
            const unique = [...new Set(
                allBranchItems
                    .filter(item => item.category === tabName)
                    .map(item => item.classification)
                    .filter(Boolean)
            )].sort()

            setAvailableClassifications(unique)
            if (unique.length === 1) setActiveClassification(unique[0])
            return
        }

        try {
            const { data: branchItems, error: branchError } = await supabase
                .from('branch_menu_items')
                .select('item_id')
                .eq('branch_id', staffBranchId)

            if (branchError) throw branchError

            const branchItemIds = (branchItems || []).map(r => r.item_id)

            if (branchItemIds.length === 0) {
                setAvailableClassifications([])
                return
            }

            const { data, error } = await supabase
                .from('menu_items')
                .select('classification')
                .eq('category', tabName)
                .eq('is_archived', false)
                .in('item_id', branchItemIds)

            if (error) throw error

            const unique = [...new Set(
                (data || []).map(r => r.classification).filter(Boolean)
            )].sort()

            setAvailableClassifications(unique)

            if (unique.length === 1) {
                setActiveClassification(unique[0])
            }
        } catch (err) {
            console.error('Error fetching classifications:', err)
            setAvailableClassifications([])
        }
    }

    const handleSelectClassification = (classification) => {
        setActiveClassification(classification)
        setShowClassificationModal(false)
    }

    const fetchMenuItems = async (category, classification, branchId) => {
        try {
            setLoadingMenu(true)
            console.log('[fetchMenuItems]', { isOnline, category, classification, branchId })

            if (!isOnline) {
                const cached = await offlineDB.menuCache.where('branch_id').equals(branchId).toArray()
                const filtered = cached.filter(item => item.category === category && item.classification === classification)
                console.log('[offline] all branch cache:', cached.length, 'filtered:', filtered.length)
                setMenuItems(filtered)
                return
            }

            const { data, error } = await supabase
                .from('branch_menu_items')
                .select(`
                    item_id,
                    branch_price,
                    menu_items (
                        item_id,
                        name,
                        item_price,
                        category,
                        classification,
                        item_img,
                        is_archived
                    )
                `)
                .eq('branch_id', branchId)
                .eq('menu_items.category', category)
                .eq('menu_items.classification', classification)

            if (error) throw error

            const flattenedItems = (data || [])
                .map(row => {
                    const item = row.menu_items
                    if (!item || item.is_archived) return null
                    return {
                        ...item,
                        item_price: row.branch_price != null ? row.branch_price : item.item_price,
                    }
                })
                .filter(Boolean)

            if (flattenedItems.length === 0) {
                setMenuItems([])
                return
            }

            const availabilityMap = await computeItemAvailability(flattenedItems, branchId)
            const itemsWithStock = flattenedItems.map(item => ({
                ...item,
                isAvailable: availabilityMap.get(item.item_id) ?? false,
            }))

            setMenuItems(itemsWithStock)
            await offlineDB.menuCache.bulkPut(
                itemsWithStock.map(item => ({ ...item, branch_id: branchId }))
            )
        } catch (error) {
            console.error('Error fetching menu items:', error)
            setMenuItems([])
        } finally {
            setLoadingMenu(false)
        }
    }

    const fetchAllBranchItems = async (branchId) => {
        try {
            if (!isOnline) {
                const cached = await offlineDB.menuCache.where({ branch_id: branchId }).toArray()
                setAllBranchItems(cached)
                return cached
            }
            // const { data, error } = await supabase
            //     .from('branch_menu_items')
            //     .select(`
            //         item_id,
            //         branch_price,
            //         menu_items (
            //             item_id,
            //             name,
            //             item_price,
            //             category,
            //             classification,
            //             item_img,
            //             is_archived
            //         )
            //     `)
            //     .eq('branch_id', branchId)

            // if (error) throw error

            // const flattened = (data || [])
            //      .map(row => {
            //         const item = row.menu_items
            //         if (!item || item.is_archived) return null
            //         return {
            //             ...item,
            //             item_price: row.branch_price != null ? row.branch_price : item.item_price,
            //         }
            //     })
            //     .filter(Boolean)

            // const availabilityMap = await computeItemAvailability(flattened, branchId)
            // const flattenedWithStock = flattened.map(item => ({
            //     ...item,
            //     isAvailable: availabilityMap.get(item.item_id) ?? false,
            // }))

            // setAllBranchItems(flattenedWithStock)
            // await offlineDB.menuCache.bulkPut(flattened.map(item => ({ ...item, branch_id: branchId })))
            // return flattenedWithStock 

            const res = await fetch(`${API_BASE_URL}/api/pos/branch/${branchId}/menu`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch branch menu')
            const { items } = await res.json()

            setAllBranchItems(items)
            await offlineDB.menuCache.bulkPut(items.map(item => ({ ...item, branch_id: branchId })))
            return items

        } catch (err) {
            console.error('Error fetching all branch items:', err)
        }
    }

    const fetchOpenCashDrawerSession = async () => {
        try {
            if (!isOnline) {
                const cached = await getOpenCashDrawerSessionOffline(staffBranchId)
                setCashDrawerSession(cached)
                setCashDrawerTransactions(cached ? await getCashDrawerTransactionsOffline(cached.session_id) : [])
                return
            }
            const { data, error } = await supabase
                .from('cash_drawer_sessions')
                .select('*')
                .eq('branch_id', staffBranchId)
                .eq('status', 'open')
                .order('opened_at', { ascending: false })
                .limit(1)
                .maybeSingle()
            if (error) throw error
            setCashDrawerSession(data || null)
            await cacheCashDrawerSession(staffBranchId, data || null)
            if (data) fetchCashDrawerTransactions(data.session_id)
        } catch (err) {
            console.error('Error fetching cash drawer session:', err)
        }
    }

    const fetchCashDrawerTransactions = async (sessionId) => {
        try {
            const { data, error } = await supabase
                .from('cash_drawer_transactions')
                .select('*')
                .eq('session_id', sessionId)
                .order('created_at', { ascending: false })
            if (error) throw error
            setCashDrawerTransactions(data || [])
            await cacheCashDrawerTransactions(sessionId, data || [])
        } catch (err) {
            console.error('Error fetching cash drawer transactions:', err)
        }
    }

    const validateStockAvailability = async (cartItems, branchId) => {
        const itemIds = [...new Set(cartItems.map(item => item.item_id))]
        const { data: recipes, error: recipeError } = await supabase
            .schema('inventory').from('menu_item_ingredients')
            .select('item_id, ingredient_id, quantity_used, unit').in('item_id', itemIds)
        if (recipeError) throw recipeError
        const allVariantIds = cartItems.flatMap(item => (item.variantObjects || []).map(v => v.variant_id))
        let variantRules = []
        if (allVariantIds.length > 0) {
            const { data: rules, error: rulesError } = await supabase
                .schema('inventory').from('menu_item_variant_ingredient_rules')
                .select('item_id, variant_id, ingredient_id, quantity_delta, unit')
                .in('item_id', itemIds).in('variant_id', allVariantIds)
            if (rulesError) throw rulesError
            variantRules = rules || []
        }
        const { data: inventory, error: inventoryError } = await supabase
            .schema('inventory').from('branch_inventory')
            .select('ingredient_id, current_stock').eq('branch_id', branchId)
        if (inventoryError) throw inventoryError
        const { data: reservations, error: reservationsError } = await supabase
            .schema('inventory').from('stock_reservations')
            .select('ingredient_id, reserved_quantity')
            .eq('branch_id', branchId).eq('status', 'reserved')
        if (reservationsError) throw reservationsError
        const reservedMap = new Map()
        for (const r of reservations || []) {
            reservedMap.set(r.ingredient_id, (reservedMap.get(r.ingredient_id) || 0) + Number(r.reserved_quantity || 0))
        }
        const availableMap = new Map()
        for (const inv of inventory || []) {
            const reserved = reservedMap.get(inv.ingredient_id) || 0
            availableMap.set(inv.ingredient_id, Number(inv.current_stock || 0) - reserved)
        }
        const requiredMap = new Map()
        for (const cartItem of cartItems) {
            const activeStatuses = new Set(['active', 'void_rejected'])
            if (!activeStatuses.has(cartItem.line_status || 'active')) continue
            const qty = parseInt(cartItem.qty, 10) || 0
            if (qty <= 0) continue
            const selectedVariantIds = (cartItem.variantObjects || []).map(v => v.variant_id)
            const itemRecipes = (recipes || []).filter(r => r.item_id === cartItem.item_id)
            for (const r of itemRecipes) {
                const needed = Number(r.quantity_used || 0) * qty
                requiredMap.set(r.ingredient_id, (requiredMap.get(r.ingredient_id) || 0) + needed)
            }
            if (selectedVariantIds.length > 0) {
                const applicableRules = variantRules.filter(rule =>
                    rule.item_id === cartItem.item_id && selectedVariantIds.includes(rule.variant_id))
                for (const rule of applicableRules) {
                    const delta = Number(rule.quantity_delta || 0) * qty
                    requiredMap.set(rule.ingredient_id, (requiredMap.get(rule.ingredient_id) || 0) + delta)
                }
            }
        }
        const insufficient = []
        for (const [ingredientId, requiredQty] of requiredMap.entries()) {
            if (requiredQty <= 0) continue
            const available = availableMap.get(ingredientId)
            if (available === undefined) {
                insufficient.push({ ingredient_id: ingredientId, required: requiredQty, available: 0, reason: 'not_tracked' })
                continue
            }
            if (requiredQty > available) insufficient.push({ ingredient_id: ingredientId, required: requiredQty, available })
        }
        return insufficient
    }

    const computeItemAvailability = async (items, branchId) => {
        const itemIds = items.map(i => i.item_id)
        if (itemIds.length === 0) return new Map()

        const { data: recipes, error: recipesError } = await supabase
            .schema('inventory')
            .from('menu_item_ingredients')
            .select('item_id, ingredient_id, quantity_used')
            .in('item_id', itemIds)
        if (recipesError) throw recipesError

        const { data: inventory, error: inventoryError } = await supabase
            .schema('inventory')
            .from('branch_inventory')
            .select('ingredient_id, current_stock')
            .eq('branch_id', branchId)
        if (inventoryError) throw inventoryError

        const { data: reservations, error: reservationsError } = await supabase
            .schema('inventory')
            .from('stock_reservations')
            .select('ingredient_id, reserved_quantity')
            .eq('branch_id', branchId)
            .eq('status', 'reserved')
        if (reservationsError) throw reservationsError

        const reservedMap = new Map()
        for (const r of reservations || []) {
            reservedMap.set(r.ingredient_id, (reservedMap.get(r.ingredient_id) || 0) + Number(r.reserved_quantity || 0))
        }

        const inventoryMap = new Map()
        for (const inv of inventory || []) {
            const reserved = reservedMap.get(inv.ingredient_id) || 0
            inventoryMap.set(inv.ingredient_id, Number(inv.current_stock || 0) - reserved)
        }

        const availabilityMap = new Map()
        for (const item of items) {
            const itemRecipes = (recipes || []).filter(r => r.item_id === item.item_id)
            if (itemRecipes.length === 0) {
                availabilityMap.set(item.item_id, false)
                continue
            }
            let canMake = true
            for (const r of itemRecipes) {
                const available = inventoryMap.get(r.ingredient_id)
                if (available === undefined || available < Number(r.quantity_used || 0)) { canMake = false; break }
            }
            availabilityMap.set(item.item_id, canMake)
        }
        return availabilityMap
    }

    const fetchCustomization = async (item) => {
        try {
            setLoadingCustomization(true)

            if (!isOnline) {
                const [cachedVariants, cachedModifiers] = await Promise.all([
                    offlineDB.variantCache.where('item_id').equals(item.item_id).toArray(),
                    offlineDB.modifierCache.where('item_id').equals(item.item_id).toArray(),
                ])
                setVariants(cachedVariants)
                setModifiers(cachedModifiers)
                return
            }

            const [{ data: variantData, error: vErr }, { data: menuModifierData, error: mmErr }] = await Promise.all([
                supabase.from('menu_variants').select(`id, variant_id, item_variants (variant_id, variant_name, category, price, is_archived)`).eq('item_id', item.item_id),
                supabase.from('menu_modifiers').select(`id, modifier_id, item_modifiers (modifier_id, modifier_name, category, price, is_archived)`).eq('item_id', item.item_id)
            ])
            if (vErr) throw vErr
            if (mmErr) throw mmErr
            setVariants((variantData || []).map(row => row.item_variants).filter(v => v && !v.is_archived))
            setModifiers((menuModifierData || []).map(row => row.item_modifiers).filter(m => m && !m.is_archived))
        } catch (error) {
            console.error('Error fetching customization:', error)
            setVariants([]); setModifiers([])
        } finally {
            setLoadingCustomization(false)
        }
    }

    const fetchUserData = async () => {
        try {
            const userEmail = getAuthItem('userEmail')
            const userType = getAuthItem('userType')
            if (!userEmail || !userType) return
            if (userType === 'admin') {
                const { data: adminData } = await supabase.from('admin_accounts').select('id, email').eq('email', userEmail).single()
                if (adminData) {
                    const { data: profileData } = await supabase.from('profiles').select('first_name, last_name, profile_picture_url').eq('user_id', adminData.id).eq('user_type', 'admin').maybeSingle()
                    setUserData({ name: profileData ? `${profileData.first_name} ${profileData.last_name}` : 'Admin User', profilePicture: profileData?.profile_picture_url || null })
                }
            } else if (userType === 'staff') {
                // ── Instant paint from cache (populated by warmStaffSession at login) ──
                const cachedProfile = await offlineDB.metaCache.get('staffProfile')
                const cachedDisplay = await offlineDB.metaCache.get('staffDisplayInfo')

                if (cachedProfile?.value?.branch_id) {
                    setStaffBranchId(cachedProfile.value.branch_id)
                    setLoggedInStaffId(cachedProfile.value.id)

                    const cachedItems = await offlineDB.menuCache
                        .where('branch_id').equals(cachedProfile.value.branch_id).toArray()
                    if (cachedItems.length) setAllBranchItems(cachedItems)

                    setUserData({
                        name: cachedDisplay?.value?.name
                            || `${cachedProfile.value.first_name} ${cachedProfile.value.last_name}`,
                        profilePicture: cachedDisplay?.value?.profilePicture || null
                    })
                }

                const ctx = await getBranchStaffContext(isOnline)
                const staffId = ctx.id
                const staffBranchIdVal = ctx.branch_id

                // still need first/last name for the display-name fallback below
                let staffFirstName = '', staffLastName = ''
                const cachedProfileForName = await offlineDB.metaCache.get('staffProfile')
                if (cachedProfileForName?.value) {
                    staffFirstName = cachedProfileForName.value.first_name
                    staffLastName = cachedProfileForName.value.last_name
                }
                if (isOnline) {
                    const { data: staffRow } = await supabase
                        .from('staff')
                        .select('first_name, last_name')
                        .eq('id', staffId)
                        .maybeSingle()
                    if (staffRow) {
                        staffFirstName = staffRow.first_name
                        staffLastName = staffRow.last_name
                        await offlineDB.metaCache.put({
                            key: 'staffProfile',
                            value: { id: staffId, branch_id: staffBranchIdVal, first_name: staffFirstName, last_name: staffLastName }
                        })
                    }
                }

                setStaffBranchId(staffBranchIdVal || null)
                setLoggedInStaffId(staffId)

                if (staffBranchIdVal) {
                    const cachedItems = await offlineDB.menuCache.where('branch_id').equals(staffBranchIdVal).toArray()
                    if (cachedItems.length) setAllBranchItems(cachedItems)
                }

                let displayName = `${staffFirstName} ${staffLastName}`
                let profilePicture = null

                if (isOnline) {
                    const { data: profileData } = await supabase
                        .from('profiles')
                        .select('first_name, last_name, profile_picture_url')
                        .eq('user_id', staffId)
                        .eq('user_type', 'staff')
                        .maybeSingle()
                    if (profileData) {
                        displayName = `${profileData.first_name} ${profileData.last_name}`
                        profilePicture = profileData.profile_picture_url || null
                    }
                    await offlineDB.metaCache.put({ key: 'staffDisplayInfo', value: { name: displayName, profilePicture } })
                } else {
                    const cachedDisplay = await offlineDB.metaCache.get('staffDisplayInfo')
                    if (cachedDisplay) {
                        displayName = cachedDisplay.value.name
                        profilePicture = cachedDisplay.value.profilePicture
                    }
                }
                setUserData({ name: displayName, profilePicture })

                if (staffBranchIdVal) {
                    const items = await fetchAllBranchItems(staffBranchIdVal)
                    if (isOnline) {
                        refreshOfflineCaches(staffBranchIdVal, items).catch(err =>
                            console.error('Prefetch failed:', err)
                        )
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching user data:', error)
        }
    }

    const handleOpenModal = async (item, cartItem = null) => {
        setSelectedItem(item)
        setEditingCartKey(cartItem ? cartItem.cartKey : null)
        await fetchCustomization(item)
        if (cartItem) {
            const variantMap = {}
                ; (cartItem.variantObjects || []).forEach(variant => { variantMap[variant.category || 'Options'] = variant })
            setSelectedVariants(variantMap)
            setSelectedModifiers(cartItem.modifierObjects || [])
        } else {
            setSelectedVariants({})
            setSelectedModifiers([])
        }
    }

    const handleCloseModal = () => {
        setSelectedItem(null); setEditingCartKey(null)
        setVariants([]); setModifiers([])
        setSelectedVariants({}); setSelectedModifiers([])
    }

    const handleSelectVariant = (groupName, variant) => {
        setSelectedVariants(prev => {
            if (prev[groupName]?.variant_id === variant.variant_id) {
                const updated = { ...prev }; delete updated[groupName]; return updated
            }
            return { ...prev, [groupName]: variant }
        })
    }

    const handleToggleModifier = (modifier) => {
        setSelectedModifiers(prev => {
            const exists = prev.find(m => m.modifier_id === modifier.modifier_id)
            return exists ? prev.filter(m => m.modifier_id !== modifier.modifier_id) : [...prev, modifier]
        })
    }

    const handleToggleCartDetails = (cartKey) => {
        setExpandedCartKeys(prev => {
            const next = new Set(prev)
            next.has(cartKey) ? next.delete(cartKey) : next.add(cartKey)
            return next
        })
    }

    const groupedVariants = variants.reduce((acc, v) => {
        const key = v.category || 'Options'
        if (!acc[key]) acc[key] = []
        acc[key].push(v)
        return acc
    }, {})

    const computedPrice = selectedItem
        ? selectedItem.item_price
        + Object.values(selectedVariants).reduce((sum, v) => sum + (v.price || 0), 0)
        + selectedModifiers.reduce((sum, m) => sum + (m.price || 0), 0)
        : 0

    const handleAddToCart = () => {
        if (!selectedItem) return
        const variantGroups = variants.reduce((acc, variant) => {
            const group = variant.category || 'Options'
            if (!acc[group]) acc[group] = []
            acc[group].push(variant)
            return acc
        }, {})
        const missingGroups = Object.keys(variantGroups).filter(group => !selectedVariants[group])
        if (missingGroups.length > 0) {
            Swal.fire({ icon: 'warning', title: 'Missing Required Options', text: `Please select: ${missingGroups.join(', ')}`, confirmButtonColor: '#A0826D' })
            return
        }
        const selectedVariantObjects = Object.values(selectedVariants)
        const selectedModifierObjects = selectedModifiers
        const variantLabels = selectedVariantObjects.map(v => v.variant_name)
        const modifierLabels = selectedModifierObjects.map(m => m.modifier_name)
        const cartKey = `${selectedItem.item_id}__${variantLabels.join(',')}__${modifierLabels.join(',')}`
        if (editingCartKey) {
            setCartItems(prev => prev.map(c => c.cartKey === editingCartKey
                ? { ...c, cartKey, item_id: selectedItem.item_id, name: selectedItem.name, unitPrice: computedPrice, variants: variantLabels, modifiers: modifierLabels, variantObjects: selectedVariantObjects, modifierObjects: selectedModifierObjects, line_status: c.line_status || 'active', void_reason: c.void_reason || null }
                : c))
            handleCloseModal(); return
        }
        setCartItems(prev => {
            const existing = prev.find(c => c.cartKey === cartKey)
            if (existing) return prev.map(c => c.cartKey === cartKey ? { ...c, qty: c.qty + 1 } : c)
            return [...prev, { cartKey, item_id: selectedItem.item_id, name: selectedItem.name, unitPrice: computedPrice, qty: 1, variants: variantLabels, modifiers: modifierLabels, variantObjects: selectedVariantObjects, modifierObjects: selectedModifierObjects, line_status: 'active' }]
        })
        handleCloseModal()
    }

    const handleEditCartItem = (cartItem) => {
        const matchedMenuItem = menuItems.find(item => String(item.item_id) === String(cartItem.item_id))
        if (!matchedMenuItem) { console.error('Menu item not found for editing:', cartItem.item_id); return }
        handleOpenModal(matchedMenuItem, cartItem)
    }

    const handleRequestVoid = async (cartItem) => {
        const result = await Swal.fire({
            icon: 'warning', title: 'Request Void',
            html: `<div style="text-align:left; font-size:14px;"><div style="font-weight:600; margin-bottom:4px;">${cartItem.name}</div><div>Qty: ${cartItem.qty}</div>${cartItem.variants?.length ? `<div style="color:#888; margin-top:4px;">Variants: ${cartItem.variants.join(', ')}</div>` : ''}${cartItem.modifiers?.length ? `<div style="color:#C9A687; margin-top:4px;">Add-ons: ${cartItem.modifiers.join(', ')}</div>` : ''}</div>`,
            input: 'select', inputOptions: { wrong_item: 'Wrong item entered', duplicate_entry: 'Duplicate entry', customer_changed_mind: 'Customer changed mind', out_of_stock: 'Out of stock', other: 'Other' },
            inputPlaceholder: 'Select a reason', showCancelButton: true, confirmButtonText: 'Submit', cancelButtonText: 'Cancel', confirmButtonColor: '#A0826D'
        })
        if (!result.isConfirmed || !result.value) return
        setCartItems(prev => prev.map(item => item.cartKey === cartItem.cartKey ? { ...item, line_status: 'void_requested', void_reason: result.value } : item))
    }

    const handleVoidItem = async (cartItem) => {
        const result = await Swal.fire({
            icon: 'warning', title: 'Void Item',
            html: `<div style="text-align:left; font-size:14px;"><div style="font-weight:600; margin-bottom:4px;">${cartItem.name}</div><div>Qty: ${cartItem.qty}</div>${cartItem.variants?.length ? `<div style="color:#888; margin-top:4px;">Variants: ${cartItem.variants.join(', ')}</div>` : ''}${cartItem.modifiers?.length ? `<div style="color:#C9A687; margin-top:4px;">Add-ons: ${cartItem.modifiers.join(', ')}</div>` : ''}</div>`,
            input: 'select', inputOptions: { wrong_item: 'Wrong item entered', duplicate_entry: 'Duplicate entry', customer_changed_mind: 'Customer changed mind', out_of_stock: 'Out of stock', other: 'Other' },
            inputPlaceholder: 'Select a reason', showCancelButton: true, confirmButtonText: 'Next', cancelButtonText: 'Cancel', confirmButtonColor: '#A0826D'
        })
        if (!result.isConfirmed || !result.value) return
        setPendingVoidItem({ ...cartItem, void_reason: result.value, void_requested_by: loggedInStaffId, void_requested_at: new Date().toISOString() })
        setEnteredPin(''); setShowPinModal(true)
    }

    const handleVoidSelected = async () => {
        if (selectedVoidKeys.size === 0) return
        const result = await Swal.fire({
            icon: 'warning', title: `Void ${selectedVoidKeys.size} Item(s)`, text: 'Select a reason for voiding these items.',
            input: 'select', inputOptions: { wrong_item: 'Wrong item entered', duplicate_entry: 'Duplicate entry', customer_changed_mind: 'Customer changed mind', out_of_stock: 'Out of stock', other: 'Other' },
            inputPlaceholder: 'Select a reason', showCancelButton: true, confirmButtonText: 'Next', confirmButtonColor: '#A0826D'
        })
        if (!result.isConfirmed || !result.value) return
        setPendingVoidItem({ type: 'void_selected', void_reason: result.value, void_requested_by: loggedInStaffId, void_requested_at: new Date().toISOString() })
        setEnteredPin(''); setShowPinModal(true)
    }

    const handleChangeQty = (cartKey, delta) => {
        setCartItems(prev => prev.map(item => {
            if (item.cartKey !== cartKey) return item
            const nextQty = Math.min(99, Math.max(1, (parseInt(item.qty, 10) || 1) + delta))
            return { ...item, qty: nextQty }
        }))
    }

    const handleQtyInputChange = (cartKey, value) => {
        const digitsOnly = value.replace(/\D/g, '').slice(0, 2)
        setCartItems(prev => prev.map(item => {
            if (item.cartKey !== cartKey) return item
            if (digitsOnly === '') return { ...item, qty: '' }
            const parsedQty = parseInt(digitsOnly, 10)
            if (isNaN(parsedQty)) return item
            return { ...item, qty: Math.min(99, Math.max(1, parsedQty)) }
        }))
    }

    const handleQtyInputBlur = (cartKey) => {
        setCartItems(prev => prev.map(item => {
            if (item.cartKey !== cartKey) return item
            const parsedQty = parseInt(item.qty, 10)
            return { ...item, qty: isNaN(parsedQty) || parsedQty < 1 ? 1 : Math.min(99, parsedQty) }
        }))
    }

    const payableStatuses = new Set(['active', 'void_rejected'])

    const subtotal = cartItems.reduce((sum, c) =>
        payableStatuses.has(c.line_status || 'active') ? sum + c.unitPrice * (parseInt(c.qty, 10) || 0) : sum, 0)

    const payableItemCount = cartItems.filter(c => payableStatuses.has(c.line_status || 'active')).length

    const buildReservationRowsForCartItem = async ({ cartItem, orderId, orderItemId, branchId }) => {
        const selectedVariantIds = (cartItem.variantObjects || []).map(v => v.variant_id)
        const qty = parseInt(cartItem.qty, 10) || 0
        if (qty <= 0) return []
        const grouped = new Map()
        const { data: baseIngredients, error: baseError } = await supabase.schema('inventory').from('menu_item_ingredients').select('ingredient_id, quantity_used, unit').eq('item_id', cartItem.item_id)
        if (baseError) throw baseError
        for (const ingredient of baseIngredients || []) {
            const key = `${ingredient.ingredient_id}__${ingredient.unit || ''}`
            const current = grouped.get(key) || { order_id: orderId, order_item_id: orderItemId, branch_id: branchId, ingredient_id: ingredient.ingredient_id, reserved_quantity: 0, unit: ingredient.unit || '', status: 'reserved' }
            current.reserved_quantity += (parseFloat(ingredient.quantity_used) || 0) * qty
            grouped.set(key, current)
        }
        if (selectedVariantIds.length > 0) {
            const { data: rules, error: rulesError } = await supabase.schema('inventory').from('menu_item_variant_ingredient_rules').select('ingredient_id, quantity_delta, unit, variant_id').eq('item_id', cartItem.item_id).in('variant_id', selectedVariantIds)
            if (rulesError) throw rulesError
            for (const rule of rules || []) {
                const key = `${rule.ingredient_id}__${rule.unit || ''}`
                const current = grouped.get(key) || { order_id: orderId, order_item_id: orderItemId, branch_id: branchId, ingredient_id: rule.ingredient_id, reserved_quantity: 0, unit: rule.unit || '', status: 'reserved' }
                current.reserved_quantity += (parseFloat(rule.quantity_delta) || 0) * qty
                grouped.set(key, current)
            }
        }
        return Array.from(grouped.values()).filter(row => row.reserved_quantity > 0)
    }

    const saveStockReservations = async (reservationRows) => {
        if (!reservationRows.length) return
        const { error } = await supabase.schema('inventory').from('stock_reservations').insert(reservationRows.map(row => ({ ...row, status: 'reserved' })))
        if (error) throw error
    }

    // Offline receipt numbers can't come from the DB sequence (no connection to
    // reach it), so we approximate it: look at the highest receipt_number
    // already cached for this branch and increment it, preserving whatever
    // prefix/padding format the real numbers use. The real number gets
    // reconciled once this order syncs.
    const getNextOfflineReceiptNumber = async (branchId) => {
        try {
            return await getNextOfflineReceiptNumberFromMeta(branchId)
        } catch (err) {
            console.error('Failed to compute offline receipt number:', err)
            return null
        }
    }

    const ensureCashDrawerOpen = () => {
        if (cashDrawerSession?.status === 'open') return true

        Swal.fire({
            icon: 'warning',
            title: 'Cash Drawer Closed',
            text: 'Open the cash drawer before processing a transaction.',
            showCancelButton: true,
            confirmButtonText: 'Open Cash Drawer',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#A0826D',
        }).then(result => {
            if (!result.isConfirmed) return
            if (!selectedBarista) {
                Swal.fire({ icon: 'warning', title: 'No Barista Selected', text: 'Select a barista before managing the cash drawer.', confirmButtonColor: '#A0826D' })
                return
            }
            setShowCashDrawerModal(true)
        })
        return false
    }

    const handlePay = () => {
        if (!cartItems.length || payableItemCount === 0) return

        if (activeRemakeRequest) {
            Swal.fire({
                icon: 'question',
                title: 'Confirm Remake',
                text: 'This order will be remade at no charge to the customer. Are you sure you want to proceed?',
                showCancelButton: true,
                confirmButtonText: 'Yes, remake order',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#A0826D',
            }).then(result => {
                if (result.isConfirmed) handleConfirmCheckout()
            })
            return
        }

        if (!ensureCashDrawerOpen()) return

        setSelectedPaymentMethod('cash');
        setSelectedFulfillmentType('dine_in');
        setAmountReceived('');
        setPaymentReferenceNumber('')
        setIsSplitPayment(false)
        setSplitPayments([])
        setShowPaymentModal(true)
    }


    const handleConfirmCheckout = async () => {
        // if (isSubmittingRef.current) return
        // isSubmittingRef.current = true

        // if (appliedDiscount?.type === 'campaign_voucher' && !isOnline) {
        //     Swal.fire({ icon: 'warning', title: 'Voucher Requires Connection', text: 'Campaign vouchers can only be redeemed while online, to prevent duplicate use.', confirmButtonColor: '#A0826D' })
        //     return
        // }

        if (!activeRemakeRequest && !ensureCashDrawerOpen()) return

        //Payment method / split payment validation — remakes are free, so skip all of this
        if (isSplitPayment && !activeRemakeRequest) {
            if (splitPayments.length === 0) {
                Swal.fire({ icon: 'warning', title: 'No Payment Methods', text: 'Please add at least one payment method.', confirmButtonColor: '#A0826D' })
                return
            }
            if (splitRemaining > 0) {
                Swal.fire({ icon: 'warning', title: 'Underpaid', text: `Still ₱${splitRemaining.toFixed(2)} remaining. Please cover the full amount.`, confirmButtonColor: '#A0826D' })
                return
            }
            for (const sp of splitPayments) {
                if (!sp.amount || parseFloat(sp.amount) <= 0) {
                    Swal.fire({ icon: 'warning', title: 'Invalid Amount', text: 'Each payment method must have a valid amount.', confirmButtonColor: '#A0826D' })
                    return
                }
                if (['gcash', 'card', 'bank_transfer'].includes(sp.method) && !sp.referenceNumber.trim()) {
                    Swal.fire({ icon: 'warning', title: 'Reference Number Required', text: `Please enter a reference number for ${sp.method.toUpperCase()}.`, confirmButtonColor: '#A0826D' })
                    return
                }
            }
        } else if (!activeRemakeRequest) {
            if (!selectedPaymentMethod) {
                Swal.fire({ icon: 'warning', title: 'Payment Method Required', text: 'Please select a payment method.', confirmButtonColor: '#A0826D' })
                return
            }
            if (selectedPaymentMethod === 'cash') {
                const received = parseFloat(amountReceived)
                if (isNaN(received) || received <= 0) { Swal.fire({ icon: 'warning', title: 'Invalid Amount', text: 'Please enter the amount received.', confirmButtonColor: '#A0826D' }); return }
                if (received < total) { Swal.fire({ icon: 'warning', title: 'Insufficient Payment', text: 'Amount received is less than the total amount due.', confirmButtonColor: '#A0826D' }); return }
            }
             if (['gcash', 'card', 'bank_transfer'].includes(selectedPaymentMethod) && !paymentReferenceNumber.trim()) {
                Swal.fire({ icon: 'warning', title: 'Reference Number Required', text: 'Please enter the reference number for this transaction.', confirmButtonColor: '#A0826D' })
                return
            }
        }

        if (!cartItems.length) return
        const hasPayableItems = cartItems.some(c => payableStatuses.has(c.line_status || 'active'))
        if (!hasPayableItems) {
            Swal.fire({ icon: 'warning', title: 'No Payable Items', text: 'All items in the cart have been voided.', confirmButtonColor: '#A0826D' })
            return
        }

        if (isSubmittingRef.current) return
        isSubmittingRef.current = true
        setIsProcessingPayment(true)
        const clientRef = uuidv4()

        try {
            // Stock validation: live query if online, cached snapshot if offline
            let offlineRequiredMap = null

            if (isOnline) {
                const insufficient = await validateStockAvailability(cartItems, staffBranchId)
                if (insufficient.length > 0) {
                    Swal.fire({ icon: 'error', title: 'Insufficient Stock', text: 'Some ingredients are not enough to fulfill this order.', confirmButtonColor: '#A0826D' })
                    return
                }
            } else {
                const { insufficient, requiredMap } = await validateStockOffline(cartItems)
                if (insufficient.length > 0) {
                    Swal.fire({ icon: 'error', title: 'Insufficient Stock (Offline)', text: 'Cached stock levels are not enough to fulfill this order.', confirmButtonColor: '#A0826D' })
                    return
                }
                offlineRequiredMap = requiredMap
            }

            // Staff context (live fetch if online, cached if offline)
            const staffData = await getStaffContext(isOnline)
            if (!staffData?.branch_id) throw new Error('No branch assigned to this staff account.')

            let offlineReceiptNumber = null
            if (!isOnline) {
                offlineReceiptNumber = await getNextOfflineReceiptNumber(staffData.branch_id)
            }

            const isBarista = staffData.role === 'Barista'
            const referenceNumber = `POS-${Date.now()}`
            const resolvedBaristaId = selectedBarista?.barista_id ?? null

            const orderPayload = {
                user_id: loyaltyCustomer?.customerId || null,
                staff_id: isBarista ? null : staffData.id,
                pos_processed_by: resolvedBaristaId,
                reference_number: referenceNumber,
                subtotal,
                discount,
                tax: vat,
                final_amount: total,
                order_status: 'pending',
                order_source: 'pos',
                fulfillment_type: selectedFulfillmentType,
                branch_id: staffData.branch_id,
                stock_validated_offline: !isOnline,
                created_at: new Date().toISOString(),
                receipt_number: offlineReceiptNumber,
                is_remake: !!activeRemakeRequest,
                original_order_id: activeRemakeRequest?.order_id || null,
                remake_request_id: activeRemakeRequest?.remake_request_id || null,
            }

            const transactionRows = activeRemakeRequest
                ? [{
                    payment_method: 'remake',
                    transaction_amount: 0,
                    status: 'paid',
                    created_at: orderPayload.created_at,
                }]
                : isSplitPayment
                    ? splitPayments.map(sp => ({
                        payment_method: sp.method,
                        transaction_amount: parseFloat(sp.amount),
                        status: 'paid',
                        created_at: orderPayload.created_at,
                    }))
                    : [{
                        payment_method: selectedPaymentMethod,
                        transaction_amount: total,
                        status: 'paid',
                        created_at: orderPayload.created_at,
                    }]

            const payableItems = cartItems.filter(c => payableStatuses.has(c.line_status || 'active'))

            // OFFLINE PATH — queue everything locally, show receipt, sync later
            if (!isOnline) {
                const itemsPayload = []
                const localOrderId = `local_order_${clientRef}`

                for (const cartItem of payableItems) {
                    const localItemKey = `local_item_${uuidv4()}`

                    const reservationRows = await buildReservationRowsOffline(cartItem, {
                        branchId: staffData.branch_id,
                        orderId: localOrderId,
                        orderItemId: localItemKey,
                    })

                    itemsPayload.push({
                        clientRef: uuidv4(),
                        localItemKey,
                        item_id: cartItem.item_id,
                        name: cartItem.name,
                        quantity: parseInt(cartItem.qty, 10) || 0,
                        item_price: cartItem.unitPrice,
                        discount_amount: 0,
                        discount_type: appliedDiscount?.type === 'pwd' ? appliedDiscount.discountType : 'none',
                        discount_label: appliedDiscount?.label || null,
                        discounted_quantity: 0,
                        line_status: cartItem.line_status || 'active',
                        void_reason: cartItem.void_reason || null,
                        prep_status: 'pending',
                        variantObjects: cartItem.variantObjects || [],
                        modifierObjects: cartItem.modifierObjects || [],
                        reservationRows,
                    })
                }

                await enqueueAction('create_order', {
                    clientRef,
                    order: { ...orderPayload, client_ref: clientRef },
                    items: itemsPayload,
                    transactions: transactionRows,
                    appliedDiscount,
                })

                // Points are NOT accrued at checkout anymore. The order carries
                // user_id, and OrderManagement credits the points once a barista
                // marks the order completed (awardLoyaltyPointsForOrder).

                await offlineDB.ordersCache.put({
                    ...orderPayload,
                    order_id: localOrderId,
                    client_ref: clientRef,
                    receipt_number: offlineReceiptNumber,
                    cashier_name: (selectedBarista
                        ? `${selectedBarista.first_name} ${selectedBarista.last_name}`
                        : userData?.name) || 'Staff',
                    pos_barista_info: selectedBarista
                        ? { barista_id: selectedBarista.barista_id, first_name: selectedBarista.first_name, last_name: selectedBarista.last_name }
                        : null,
                })

                await offlineDB.orderItemsCache.bulkPut(
                    itemsPayload.map(item => ({
                        order_item_id: item.localItemKey,
                        order_id: localOrderId,
                        item_id: item.item_id,
                        name: item.name,
                        quantity: item.quantity,
                        item_price: item.item_price,
                        line_status: item.line_status,
                        prep_status: item.prep_status,
                        void_reason: item.void_reason,
                        menu_items: { name: item.name },
                        order_item_variants: (item.variantObjects || []).map(v => ({
                            variant_id: v.variant_id,
                            variant_price: v.price || 0,
                            item_variants: { variant_id: v.variant_id, variant_name: v.variant_name, category: v.category },
                        })),
                        order_item_modifiers: (item.modifierObjects || []).map(m => ({
                            modifier_id: m.modifier_id,
                            modifier_price: m.price || 0,
                            item_modifiers: { modifier_id: m.modifier_id, modifier_name: m.modifier_name },
                        })),
                    }))
                )

                await offlineDB.orderTransactionsCache.put({
                    order_id: localOrderId,
                    transactions: transactionRows,
                })

                // same outage doesn't oversell against the same cached numbers.
                if (offlineRequiredMap) await commitOfflineConsumption(offlineRequiredMap)

                const cachedBranchAddress = await offlineDB.metaCache.get('branchAddress')

                const receiptData = {
                    referenceNumber,
                    receiptNumber: offlineReceiptNumber,
                    fulfillmentType: selectedFulfillmentType,
                    paymentMethod: selectedPaymentMethod,
                    items: payableItems,
                    subtotal,
                    discount,
                    discountLabel: appliedDiscount?.label || null,
                    discountType: appliedDiscount?.type || null,
                    vat,
                    total,
                    changeAmount: selectedPaymentMethod === 'cash' ? changeAmount : 0,
                    amountReceived: selectedPaymentMethod === 'cash' ? parsedAmountReceived : null,
                    createdAt: new Date(),
                    branchAddress: cachedBranchAddress?.value || null,
                    cashier: (selectedBarista
                        ? `${selectedBarista.first_name} ${selectedBarista.last_name}`
                        : userData?.name) || 'Staff',
                    queuedOffline: true,
                }

                setShowPaymentModal(false)
                setCartItems([])
                setAppliedDiscount(null)
                setExpandedCartKeys(new Set())
                setAmountReceived('')
                setSelectedPaymentMethod('cash')
                setSelectedFulfillmentType('dine_in')
                setSelectedStickerItems(new Set(receiptData.items.map(i => i.cartKey)))
                setCompletedOrder(receiptData)
                setReceiptView('receipt')
                setShowReceiptModal(true)
                setLoyaltyCustomer(null)
                setLoyaltyQuery('')
                setLoyaltyResult(null)
                setEmailCheckDone(false)

                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('kaperk:order-created'))
                    console.log('[POS] dispatched kaperk:order-created')
                }

                if (activeRemakeRequest) {
                    await completeRemakeRequestOffline(activeRemakeRequest.remake_request_id)
                    setActiveRemakeRequest(null)
                    fetchRemakeRequests()
                }

                Swal.fire({
                    icon: 'info',
                    title: 'Order Saved Offline',
                    text: 'No connection detected — this order will sync automatically once wifi is back.',
                    confirmButtonColor: '#A0826D',
                    timer: 2500,
                })
                return
            }

            let resolvedRemakeId = null
            if (activeRemakeRequest) {
                resolvedRemakeId = await resolveId(activeRemakeRequest.remake_request_id)
                const resolvedOriginalOrderId = await resolveId(activeRemakeRequest.order_id)
                if (!resolvedRemakeId || !resolvedOriginalOrderId) {
                    throw new Error('This remake request is still syncing. Please wait a moment and try again.')
                }
                orderPayload.remake_request_id = resolvedRemakeId
                orderPayload.original_order_id = resolvedOriginalOrderId
            }

            // ONLINE PATH — delegate to backend (uses service-role key, bypasses RLS)
            const checkoutRes = await fetch(`${API_BASE_URL}/api/pos/checkout`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    orderPayload,
                    cartItems,
                    transactionRows,
                    appliedDiscount,
                    // Still sent so the backend can redeem/mark the campaign voucher
                    // against this account — but see skipPointsAccrual below.
                    loyaltyCustomerId: loyaltyCustomer?.loyaltyId || null,
                    // Points are credited at order completion, not at payment.
                    skipPointsAccrual: true,
                    clientRef,
                    remakeRequestId: resolvedRemakeId,
                }),
            })
            const checkoutData = await checkoutRes.json()
            if (!checkoutRes.ok) throw new Error(checkoutData.error || 'Checkout failed')
            const orderData = checkoutData.order

            if (orderData?.order_id) {
                await offlineDB.ordersCache.put({
                    order_id: orderData.order_id,
                    client_ref: clientRef,
                    reference_number: referenceNumber,
                    receipt_number: orderData.receipt_number ?? null,
                    branch_id: staffData.branch_id,
                    order_status: orderData.order_status || 'pending',
                    created_at: orderPayload.created_at,
                    pos_barista_info: selectedBarista
                        ? { barista_id: selectedBarista.barista_id, first_name: selectedBarista.first_name, last_name: selectedBarista.last_name }
                        : null,
                })
                if (orderData.receipt_number) {
                    await setLastKnownReceiptNumber(staffData.branch_id, orderData.receipt_number)
                }
            }

            // Fetch branch address and barista name for receipt
            const { data: branchData } = await supabase
                .from('branches')
                .select('address')
                .eq('branch_id', staffData.branch_id)
                .single()

            let cashierName = null
            if (resolvedBaristaId) {
                const { data: baristaData } = await supabase
                    .from('barista_info')
                    .select('first_name, last_name')
                    .eq('barista_id', resolvedBaristaId)
                    .single()
                if (baristaData) cashierName = `${baristaData.first_name} ${baristaData.last_name}`
            }
            if (!cashierName && userData?.name) cashierName = userData.name

            const receiptData = {
                referenceNumber,
                fulfillmentType: selectedFulfillmentType,
                paymentMethod: activeRemakeRequest ? 'remake' : selectedPaymentMethod,
                items: payableItems,
                subtotal,
                discount,
                discountLabel: appliedDiscount?.label || null,
                discountType: appliedDiscount?.type || null,
                vat,
                total,
                changeAmount: activeRemakeRequest ? 0 : (selectedPaymentMethod === 'cash' ? changeAmount : 0),
                amountReceived: activeRemakeRequest ? null : (selectedPaymentMethod === 'cash' ? parsedAmountReceived : null),
                createdAt: new Date(),
                branchAddress: branchData?.address || null,
                cashier: cashierName,
                queuedOffline: false,
            }

            setShowPaymentModal(false)
            setCartItems([])
            setAppliedDiscount(null)
            setExpandedCartKeys(new Set())
            setAmountReceived('')
            setSelectedPaymentMethod('cash')
            setSelectedFulfillmentType('dine_in')
            setSelectedStickerItems(new Set(receiptData.items.map(i => i.cartKey)))
            setCompletedOrder(receiptData)
            setReceiptView('receipt')
            setShowReceiptModal(true)
            setLoyaltyCustomer(null)
            setLoyaltyQuery('')
            setLoyaltyResult(null)
            setEmailCheckDone(false)

            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('kaperk:order-created'))
            }

            if (activeRemakeRequest) {
                setActiveRemakeRequest(null)
                fetchRemakeRequests()
            }

        } catch (error) {
            console.error('Error saving POS order:', error)
            Swal.fire({ icon: 'error', title: 'Failed to Save Order', text: error.message || 'An error occurred while saving the order.', confirmButtonColor: '#A0826D' })
        } finally {
            setIsProcessingPayment(false)
            isSubmittingRef.current = false
        }
    }

    // const verifyAdminPin = async () => {
    //     try {
    //         if (!enteredPin || enteredPin.length < 4) { Swal.fire({ icon: 'warning', title: 'PIN Required', text: 'Please enter a valid Admin PIN.', confirmButtonColor: '#A0826D' }); return }
    //         const { data: adminStaff, error: adminStaffError } = await supabase.from('staff').select('email').eq('branch_id', staffBranchId).eq('role', 'Admin').limit(1).single()
    //         if (adminStaffError) throw adminStaffError
    //         const { data: adminAccount, error: adminAccountError } = await supabase.from('staff_accounts').select('pin_hash').eq('email', adminStaff.email).single()
    //         if (adminAccountError) throw adminAccountError
    //         if (!adminAccount?.pin_hash) { Swal.fire({ icon: 'warning', title: 'No Admin PIN Found', text: 'The assigned Admin has not set a PIN yet.', confirmButtonColor: '#A0826D' }); return }
    //         const isMatch = await bcrypt.compare(enteredPin, adminAccount.pin_hash)
    //         if (!isMatch) { Swal.fire({ icon: 'error', title: 'Invalid PIN', text: 'The PIN you entered is incorrect.', confirmButtonColor: '#FF6F3C' }); return }
    //         proceedVoid()
    //     } catch (err) {
    //         console.error('Error verifying admin PIN:', err)
    //         Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to verify PIN.', confirmButtonColor: '#FF6F3C' })
    //     }
    // }

    const verifyAdminPin = async () => {
        try {
            if (!enteredPin || enteredPin.length < 4) {
                Swal.fire({ icon: 'warning', title: 'PIN Required', text: 'Please enter a valid Admin PIN.', confirmButtonColor: '#A0826D' })
                return
            }

            if (!isOnline) {
                const cached = await offlineDB.adminPinCache.get(staffBranchId)
                const pinHashes = cached?.pinHashes || []

                if (pinHashes.length === 0) {
                    Swal.fire({ icon: 'warning', title: 'No Cached Admin PIN', text: 'No Admin PIN was cached for offline use. Please reconnect once before going offline.', confirmButtonColor: '#A0826D' })
                    return
                }

                const matchResults = await Promise.all(pinHashes.map(hash => bcrypt.compare(enteredPin, hash)))
                const isMatch = matchResults.some(Boolean)

                if (!isMatch) {
                    Swal.fire({ icon: 'error', title: 'Invalid PIN', text: 'The PIN you entered is incorrect.', confirmButtonColor: '#FF6F3C' })
                    return
                }

                proceedVoid()
                return
            }

            // ── ONLINE PATH (unchanged) ──
            const { data: adminStaffList, error: adminStaffError } = await supabase
                .from('staff')
                .select('id, email, company_email')
                .eq('branch_id', staffBranchId)
                .eq('role', 'Admin')

            if (adminStaffError) throw adminStaffError
            if (!adminStaffList || adminStaffList.length === 0) {
                Swal.fire({ icon: 'warning', title: 'No Admin Found', text: 'No Admin is assigned to this branch.', confirmButtonColor: '#A0826D' })
                return
            }

            const adminStaffIds = adminStaffList.map(s => s.id)
            const adminEmails = adminStaffList.flatMap(s => [s.email, s.company_email]).filter(Boolean)

            const orParts = [`staff_id.in.(${adminStaffIds.join(',')})`]
            if (adminEmails.length) orParts.push(`email.in.(${adminEmails.join(',')})`)

            const { data: adminAccounts, error: adminAccountError } = await supabase
                .from('staff_accounts')
                .select('pin_hash')
                .or(orParts.join(','))

            console.log('[verifyAdminPin] adminStaffList:', adminStaffList, 'adminAccounts found:', adminAccounts)

            if (adminAccountError) throw adminAccountError

            const { data: trueAdminAccounts, error: trueAdminError } = await supabase
                .from('admin_accounts')
                .select('pin_hash')

            if (trueAdminError) throw trueAdminError

            const validHashes = [
                ...(adminAccounts || []),
                ...(trueAdminAccounts || []),
            ].map(a => a.pin_hash).filter(Boolean)

            if (validHashes.length === 0) {
                Swal.fire({ icon: 'warning', title: 'No Admin PIN Found', text: 'No Admin in this branch has set a PIN yet.', confirmButtonColor: '#A0826D' })
                return
            }

            console.log('[verifyAdminPin] enteredPin:', JSON.stringify(enteredPin), 'validHashes:', validHashes)
            const matchResults = await Promise.all(validHashes.map(hash => bcrypt.compare(enteredPin, hash)))
            console.log('[verifyAdminPin] matchResults:', matchResults)
            const isMatch = matchResults.some(Boolean)

            if (!isMatch) {
                Swal.fire({ icon: 'error', title: 'Invalid PIN', text: 'The PIN you entered is incorrect.', confirmButtonColor: '#FF6F3C' })
                return
            }

            proceedVoid()
        } catch (err) {
            console.error('Error verifying admin PIN:', err)
            Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to verify PIN.', confirmButtonColor: '#FF6F3C' })
        }
    }

    const proceedVoid = () => {
        if (!pendingVoidItem) return
        const voidDecidedAt = new Date().toISOString()
        if (pendingVoidItem.type === 'void_selected') {
            setCartItems(prev => prev.map(item => selectedVoidKeys.has(item.cartKey) ? { ...item, line_status: 'voided', prep_status: 'voided', void_reason: pendingVoidItem.void_reason, void_requested_by: pendingVoidItem.void_requested_by, void_requested_at: pendingVoidItem.void_requested_at, void_decided_at: voidDecidedAt } : item))
            setSelectedVoidKeys(new Set()); setVoidSelectionMode(false)
        } else {
            setCartItems(prev => prev.map(item => item.cartKey === pendingVoidItem.cartKey ? { ...item, line_status: 'voided', prep_status: 'voided', void_reason: pendingVoidItem.void_reason, void_requested_by: pendingVoidItem.void_requested_by, void_requested_at: pendingVoidItem.void_requested_at, void_decided_at: voidDecidedAt } : item))
        }
        setShowPinModal(false); setEnteredPin(''); setPendingVoidItem(null)
        Swal.fire({ icon: 'success', title: pendingVoidItem.type === 'void_selected' ? 'Items Voided' : 'Item Voided', text: pendingVoidItem.type === 'void_selected' ? `${selectedVoidKeys.size} item(s) have been marked as voided.` : 'The selected item has been marked as voided.', confirmButtonColor: '#A0826D' })
    }

    const discount = (() => {
        if (!appliedDiscount) return 0
        if (appliedDiscount.type === 'pwd') {
            const keys = appliedDiscount.selectedCartKeys
            if (!keys || keys.size === 0) return 0
            return cartItems.reduce((sum, c) => {
                if (!payableStatuses.has(c.line_status || 'active')) return sum
                if (!keys.has(c.cartKey)) return sum
                return sum + c.unitPrice * (parseInt(c.qty, 10) || 0) * appliedDiscount.rate
            }, 0)
        }

        if (appliedDiscount.type === 'campaign_voucher') {
            if (appliedDiscount.rewardValueType === 'bonus_points') return 0 // grants points, doesn't discount the bill
            const applicableSubtotal = appliedDiscount.applicationScope === 'category'
                ? cartItems.reduce((sum, c) => {
                    if (!payableStatuses.has(c.line_status || 'active')) return sum
                    const menuItem = menuItems.find(m => String(m.item_id) === String(c.item_id))
                        || allBranchItems.find(m => String(m.item_id) === String(c.item_id))
                    if (menuItem?.category !== appliedDiscount.appliesToCategory) return sum
                    return sum + c.unitPrice * (parseInt(c.qty, 10) || 0)
                }, 0)
                : subtotal
            if (appliedDiscount.rewardValueType === 'fixed_amount') {
                return Math.min(Number(appliedDiscount.rewardValue) || 0, applicableSubtotal)
            }
            if (appliedDiscount.rewardValueType === 'percentage') {
                const raw = applicableSubtotal * (Number(appliedDiscount.rewardValue) / 100)
                return appliedDiscount.maxDiscountAmount ? Math.min(raw, Number(appliedDiscount.maxDiscountAmount)) : raw
            }
            return 0
        }

        return subtotal * appliedDiscount.rate
    })()

    const total = subtotal - discount
    const vat = total * (12 / 112)
    const parsedAmountReceived = parseFloat(amountReceived) || 0
    const changeAmount = selectedPaymentMethod === 'cash'
        ? Number(Math.max(0, parsedAmountReceived - total).toFixed(2))
        : 0
    const splitTotal = splitPayments.reduce((sum, sp) => sum + (parseFloat(sp.amount) || 0), 0)
    const splitRemaining = Number(Math.max(0, total - splitTotal).toFixed(2))
    const splitCashEntry = splitPayments.find(sp => sp.method === 'cash')
    const splitChange = splitCashEntry
        ? Number(Math.max(0, (parseFloat(splitCashEntry.amount) || 0) - (splitCashEntry.allocatedAmount || 0)).toFixed(2))
        : 0

    // const handleRemoveDiscount = () => { setDiscountCode(''); setShowDiscountSection(false) }

    const getLineStatusMeta = (status) => {
        switch (status) {
            case 'void_requested': return { label: 'Void Requested', className: 'bg-yellow-100 text-yellow-800' }
            case 'void_approved': return { label: 'Void Approved', className: 'bg-blue-100 text-blue-800' }
            case 'void_rejected': return { label: 'Void Rejected', className: 'bg-red-100 text-red-800' }
            case 'voided': return { label: 'Voided', className: 'bg-gray-200 text-gray-700' }
            default: return { label: 'Active', className: 'bg-green-100 text-green-800' }
        }
    }

    const handleLogout = () => { sessionStorage.removeItem('userEmail'); sessionStorage.removeItem('userType'); window.location.href = '/login' }

    const displayName = selectedBarista
        ? `${selectedBarista.first_name} ${selectedBarista.last_name}`
        : (userData?.name || 'User')

    const GRID_COLS = 5
    const placeholderCount = menuItems.length < GRID_COLS * 3 ? Math.max(0, GRID_COLS * 3 - menuItems.length) : 0

    const handlePrintReceipt = async () => {
        if (printerMode === 'bluetooth' && btChar && completedOrder) {
            try {
                const bytes = buildReceiptBytes(
                    {
                        reference_number: completedOrder.referenceNumber,
                        created_at: completedOrder.createdAt,
                        fulfillment_type: completedOrder.fulfillmentType,
                        order_source: 'pos',
                        subtotal: completedOrder.subtotal,
                        discount: completedOrder.discount,
                        final_amount: completedOrder.total,
                    },
                    completedOrder.items.map(i => ({
                        menu_items: { name: i.name },
                        quantity: i.qty,
                        item_price: i.unitPrice,
                        order_item_variants: (i.variants || []).map(v => ({ item_variants: { variant_name: v } })),
                        order_item_modifiers: (i.modifiers || []).map(m => ({ item_modifiers: { modifier_name: m } })),
                    })),
                    { payment_method: completedOrder.paymentMethod, status: 'paid' },
                    '', // branchName already baked into completedOrder if needed
                    completedOrder.branchAddress || '',
                    completedOrder.cashier || ''
                )
                for (let i = 0; i < bytes.length; i += 128) {
                    const chunk = bytes.slice(i, i + 128)
                    if (btChar.properties.writeWithoutResponse) await btChar.writeValueWithoutResponse(chunk)
                    else await btChar.writeValue(chunk)
                    await new Promise(r => setTimeout(r, 20))
                }
                Swal.fire({ icon: 'success', title: 'Printed!', text: 'Receipt sent to printer.', confirmButtonColor: '#A0826D', timer: 1800 })
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Print Failed', text: err.message, confirmButtonColor: '#A0826D' })
            }
            return
        }
        // fallback to system print
        document.body.classList.add('print-receipt')
        window.print()
        setTimeout(() => document.body.classList.remove('print-receipt'), 1000)
    }

    const handlePrintStickers = async () => {
        if (selectedStickerItems.size === 0) return

        const selectedItems = completedOrder.items.filter(item =>
            selectedStickerItems.has(item.cartKey)
        )

        if (printerMode === 'bluetooth' && btChar) {
            try {
                for (const item of selectedItems) {
                    const qty = parseInt(item.qty, 10) || 1
                    for (let i = 0; i < qty; i++) {
                        const bytes = buildStickerBytes(
                            {
                                menu_items: { name: item.name },
                                order_item_variants: (item.variants || []).map(v => ({ item_variants: { variant_name: v } })),
                                order_item_modifiers: (item.modifiers || []).map(m => ({ item_modifiers: { modifier_name: m } })),
                            },
                            {
                                reference_number: completedOrder.referenceNumber,
                                fulfillment_type: completedOrder.fulfillmentType,
                            }
                        )
                        for (let j = 0; j < bytes.length; j += 128) {
                            const chunk = bytes.slice(j, j + 128)
                            if (btChar.properties.writeWithoutResponse) await btChar.writeValueWithoutResponse(chunk)
                            else await btChar.writeValue(chunk)
                            await new Promise(r => setTimeout(r, 20))
                        }
                        // Small delay between stickers
                        await new Promise(r => setTimeout(r, 200))
                    }
                }
                Swal.fire({ icon: 'success', title: 'Printed!', text: 'Stickers sent to printer.', confirmButtonColor: '#A0826D', timer: 1800 })
            } catch (err) {
                Swal.fire({ icon: 'error', title: 'Print Failed', text: err.message, confirmButtonColor: '#A0826D' })
            }
            return
        }

        const printArea = document.getElementById('pos-sticker-print')
        const stickerContent = document.getElementById('sticker-content')
        if (!printArea || !stickerContent) return
        printArea.innerHTML = stickerContent.innerHTML
        document.body.classList.add('print-pos-sticker')
        window.print()
        setTimeout(() => {
            document.body.classList.remove('print-pos-sticker')
            printArea.innerHTML = ''
        }, 1000)
    }

    // const handlePrintStickers = () => {
    //     if (selectedStickerItems.size === 0) return
    //     const printArea = document.getElementById('pos-sticker-print')
    //     const stickerContent = document.getElementById('sticker-content')
    //     if (!printArea || !stickerContent) return
    //     printArea.innerHTML = stickerContent.innerHTML
    //     document.body.classList.add('print-pos-sticker')
    //     window.print()
    //     setTimeout(() => {
    //         document.body.classList.remove('print-pos-sticker')
    //         printArea.innerHTML = ''
    //     }, 1000)
    // }

    const toggleStickerItem = (cartKey) => {
        setSelectedStickerItems(prev => {
            const next = new Set(prev)
            next.has(cartKey) ? next.delete(cartKey) : next.add(cartKey)
            return next
        })
    }

    const filteredMenuItems = menuSearchQuery.trim()
        ? allBranchItems.filter(item =>
            item.name.toLowerCase().includes(menuSearchQuery.toLowerCase())
        )
        : menuItems.filter(item =>
            item.name.toLowerCase().includes(menuSearchQuery.toLowerCase())
        )

    return (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
            <Helmet><title>KaPerk</title></Helmet>
            <style>{`
                @media print {
                    body > * { display: none !important; }
                }
                #pos-receipt-print { display: none; }
                #pos-sticker-print { display: none; }
                body.print-receipt #pos-receipt-print { display: block !important; }
                body.print-pos-sticker #pos-sticker-print { display: block !important; }
            `}</style>
            <div id="pos-sticker-print" />
            <div id="pos-receipt-print">
                {completedOrder && (
                    <ReceiptLayout
                        order={completedOrder}
                        items={completedOrder.items}
                        transaction={null}
                        branchName=""
                        branchAddress={completedOrder.branchAddress}
                        cashier={completedOrder.cashier}
                    />
                )}
            </div>

            {/* Customization Modal */}
            {selectedItem && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleCloseModal} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[85vh]">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <div className="flex items-center gap-4">
                                {selectedItem.item_img && (
                                    <img src={selectedItem.item_img} alt={selectedItem.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                                )}
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-800">{selectedItem.name}</h2>
                                    <p className="text-sm text-gray-400">Base price: ₱{Number(selectedItem.item_price).toFixed(2)}</p>
                                </div>
                            </div>
                            <button onClick={handleCloseModal} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors text-gray-500 flex-shrink-0">✕</button>
                        </div>
                        <div className="overflow-y-auto flex-1 p-6 space-y-6">
                            {loadingCustomization ? (
                                <div className="text-center text-gray-400 text-sm py-8">Loading options...</div>
                            ) : (
                                <>
                                    {Object.keys(groupedVariants).length > 0 && (
                                        <div className="space-y-5">
                                            {Object.entries(groupedVariants).map(([groupName, groupVariants]) => {
                                                const isGroupSelected = !!selectedVariants[groupName]
                                                return (
                                                    <div key={groupName}>
                                                        <div className="flex items-center justify-between mb-3">
                                                            <div className="flex items-center gap-2">
                                                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{groupName}</h3>
                                                                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Pick one</span>
                                                            </div>
                                                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${isGroupSelected ? 'bg-[#C9A687]/15 text-[#8B6344]' : 'bg-gray-100 text-gray-400'}`}>
                                                                {isGroupSelected ? '1' : '0'}/1
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-2">
                                                            {groupVariants.map(variant => {
                                                                const isSelected = selectedVariants[groupName]?.variant_id === variant.variant_id
                                                                return (
                                                                    <button key={variant.variant_id} onClick={() => handleSelectVariant(groupName, variant)}
                                                                        className={`px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all ${isSelected ? 'border-[#C9A687] bg-[#C9A687]/10 text-[#8B6344]' : 'border-gray-200 bg-white text-gray-600 hover:border-[#C9A687]/50'}`}>
                                                                        {variant.variant_name}
                                                                        {variant.price > 0 && <span className="ml-1.5 text-xs opacity-60">+₱{Number(variant.price).toFixed(2)}</span>}
                                                                    </button>
                                                                )
                                                            })}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}
                                    {Object.keys(groupedVariants).length > 0 && modifiers.length > 0 && <hr className="border-gray-100" />}
                                    {modifiers.length > 0 && (
                                        <div>
                                            <div className="flex items-center gap-2 mb-3">
                                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Add-ons</h3>
                                                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Optional, pick many</span>
                                                {selectedModifiers.length > 0 && <span className="text-xs font-semibold bg-[#C9A687]/15 text-[#8B6344] px-2.5 py-1 rounded-full ml-auto">{selectedModifiers.length} selected</span>}
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {modifiers.map(modifier => {
                                                    const isSelected = selectedModifiers.some(m => m.modifier_id === modifier.modifier_id)
                                                    return (
                                                        <button key={modifier.modifier_id} onClick={() => handleToggleModifier(modifier)}
                                                            className={`px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all ${isSelected ? 'border-[#C9A687] bg-[#C9A687]/10 text-[#8B6344]' : 'border-gray-200 bg-white text-gray-600 hover:border-[#C9A687]/50'}`}>
                                                            {modifier.modifier_name}
                                                            {modifier.price > 0 && <span className="ml-1.5 text-xs opacity-60">+₱{Number(modifier.price).toFixed(2)}</span>}
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    {Object.keys(groupedVariants).length === 0 && modifiers.length === 0 && (
                                        <div className="text-center text-gray-400 text-sm py-8">No variants or add-ons available for this item.</div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="p-6 border-t border-gray-100">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-sm text-gray-500">Total for this item</span>
                                <span className="text-xl font-bold text-gray-800">₱{computedPrice.toFixed(2)}</span>
                            </div>
                            <button onClick={handleAddToCart} className="w-full py-3 bg-[#C9A687] text-white rounded-xl font-semibold hover:bg-[#b8956f] transition-colors shadow-sm">
                                {editingCartKey ? 'Update Item' : 'Add to Cart'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Top Navigation Bar */}
            <div className="bg-[#303030] px-6 py-4 flex justify-between items-center w-full">
                <div className="flex items-center gap-4">
                    {(showProfile || onBack) && (
                        <button onClick={() => { if (showProfile) setShowProfile(false); else if (onBack) onBack() }} className="flex items-center gap-2 text-white hover:bg-[#b8956f] px-3 py-2 rounded-lg transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                            <span className="font-medium">Back</span>
                        </button>
                    )}
                    <div>
                        <h1 className="text-white text-xl font-medium">{showProfile ? 'Profile' : 'POS'}</h1>
                        {/* Active classification breadcrumb */}
                        {!showProfile && activeTab && activeClassification && (
                            <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="text-gray-400 text-xs">{activeTab}</span>
                                <span className="text-gray-500 text-xs">›</span>
                                <button
                                    onClick={() => handleTabClick(activeTab)}
                                    className="text-[#C9A687] text-xs font-medium hover:underline"
                                >
                                    {activeClassification}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {!isOnline && (
                    <div className="bg-amber-500 text-white text-xs font-semibold px-6 py-2 flex items-center justify-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                        Offline mode — orders are being saved locally and will sync once wifi returns
                    </div>
                )}

                {pendingSyncCount > 0 && (
                    <div className="bg-blue-500 text-white text-xs font-semibold px-6 py-2 flex items-center justify-center gap-2">
                        {pendingSyncCount} order{pendingSyncCount !== 1 ? 's' : ''} waiting to sync
                    </div>
                )}

                {!showProfile && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowRemakeListModal(true)}
                            className="relative flex items-center gap-2 text-white hover:bg-[#b8956f] px-3 py-2 rounded-lg transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 12a9 9 0 1 1-3.5-7.1M21 3v6h-6" />
                            </svg>
                            <span className="text-sm font-medium">Remake Requests</span>
                            {remakeRequests.length > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                    {remakeRequests.length}
                                </span>
                            )}
                        </button>
                    <div className="relative">
                        <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center gap-2 text-white hover:bg-[#b8956f] px-3 py-2 rounded-lg transition-colors">
                            <span className="font-medium">{displayName}</span>
                            <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center overflow-hidden cursor-pointer hover:ring-2 hover:ring-white/50 transition-all" onClick={(e) => { e.stopPropagation(); setShowUserMenu(false); setShowProfile(true) }}>
                                {userData?.profilePicture ? <img src={userData.profilePicture} alt={userData.name} className="w-full h-full object-cover" /> : <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>}
                            </div>
                            <svg className={`w-5 h-5 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </button>
                        {showUserMenu && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                                    <button onClick={() => { setShowUserMenu(false); setShowProfile(true) }} className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                                        View Profile
                                    </button>
                                    <button onClick={() => { setShowUserMenu(false); handleLogout() }} className="w-full px-4 py-2 text-left text-red-600 hover:bg-gray-100 transition-colors flex items-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                                        Logout
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                    </div>
                )}
            </div>

            {/* Main Content */}
            {showProfile ? (
                <Profile onBack={() => setShowProfile(false)} />
            ) : (
                <div className="flex flex-1 overflow-hidden flex-col lg:flex-row">
                    <div className="flex-1 flex flex-col min-w-0">
                        <div className="flex-1 bg-[#FAF8F6] px-4 pt-4 overflow-auto">
                            {menuSearchQuery.trim() ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 pb-6">                                    {filteredMenuItems.length === 0 ? (
                                        <div className="col-span-4 text-center text-gray-400 text-sm py-12">
                                            No items match "{menuSearchQuery}"
                                        </div>
                                    ) : (
                                        filteredMenuItems.map((item, i) => (
                                            <div key={i} onClick={() => item.isAvailable && handleOpenModal(item)}
                                                className={`bg-white rounded-none border p-4 flex flex-col items-center justify-center transition-all min-h-[90px] ${item.isAvailable ? 'border-gray-200 cursor-pointer hover:shadow-lg hover:border-[#C9A687] active:scale-95' : 'border-gray-100 opacity-40 cursor-not-allowed'}`}>
                                                {item.item_img && (
                                                    <img src={item.item_img} alt={item.name} className="w-14 h-14 rounded-lg object-cover mb-2" />
                                                )}
                                                <p className="text-[10px] text-gray-400 mb-1">{item.category} · {item.classification}</p>
                                                <h3 className="text-sm font-semibold text-center text-gray-800 mb-2 leading-tight">{item.name}</h3>
                                                <p className="text-sm text-green-600 font-bold">₱{Number(item.item_price).toFixed(2)}</p>
                                                {!item.isAvailable && <span className="text-[11px] text-red-500 font-semibold mt-1">Out of Stock</span>}
                                            </div>
                                        ))
                                    )}
                                </div>
                            ) : !activeClassification ? (
                                <div className="flex flex-col items-center justify-center h-full text-center gap-3">
                                    <div className="w-16 h-16 rounded-full bg-[#C9A687]/10 flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9A687" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                                    </div>
                                    <p className="text-gray-400 text-sm">Select a category below to browse the menu</p>
                                </div>
                            ) : loadingMenu ? (
                                <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading menu...</div>
                            ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 pb-6">                                    {filteredMenuItems.length === 0 && menuItems.length > 0 && (
                                        <div className="col-span-4 text-center text-gray-400 text-sm py-12">No items match "{menuSearchQuery}"</div>
                                    )}
                                    {filteredMenuItems.map((item, i) => (
                                        <div key={i} onClick={() => item.isAvailable && handleOpenModal(item)}
                                            className={`bg-white rounded-none border p-4 flex flex-col items-center justify-center transition-all min-h-[90px] ${item.isAvailable ? 'border-gray-200 cursor-pointer hover:shadow-lg hover:border-[#C9A687] active:scale-95' : 'border-gray-100 opacity-40 cursor-not-allowed'}`}>
                                            {item.item_img && (
                                                <img src={item.item_img} alt={item.name} className="w-14 h-14 rounded-lg object-cover mb-2" />
                                            )}
                                            <h3 className="text-sm font-semibold text-center text-gray-800 mb-2 leading-tight">{item.name}</h3>
                                            <p className="text-sm text-green-600 font-bold">₱{Number(item.item_price).toFixed(2)}</p>
                                            {!item.isAvailable && <span className="text-[11px] text-red-500 font-semibold mt-1">Out of Stock</span>}
                                        </div>
                                    ))}
                                    {menuItems.length === 0 && (
                                        <div className="col-span-4 text-center text-gray-400 text-sm py-12">No items found in this category.</div>
                                    )}
                                    {[...Array(placeholderCount)].map((_, i) => (
                                        <div key={`placeholder-${i}`} className="bg-white rounded-none border border-gray-200 p-4 flex flex-col items-center justify-center min-h-[90px]">
                                            <div className="h-4 w-3/4 bg-gray-100 rounded mb-2" />
                                            <div className="h-3 w-1/2 bg-gray-100 rounded" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Classification */}
                        {activeTab && availableClassifications.length > 0 && (
                            <div className="bg-white border-t border-gray-200 px-4 py-2.5">
                                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                                    <span className="text-xs text-gray-400 font-medium shrink-0 pr-1">
                                        {activeTab}:
                                    </span>
                                    {availableClassifications.map(cls => (
                                        <button
                                            key={cls}
                                            onClick={() => { setMenuSearchQuery(''); setActiveClassification(cls) }}
                                            className={`shrink-0 px-3.5 py-2.5 rounded-none text-xs font-semibold border transition-all ${activeClassification === cls
                                                    ? 'bg-[#303030] border-[#303030] text-white'
                                                    : 'bg-white border-gray-300 text-gray-600 hover:border-gray-500'
                                                }`}
                                        >
                                            {cls}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Category Tabs + Search */}
                        <div className="bg-[#FAF8F6] px-6 py-4">
                            <div className="flex flex-col sm:flex-row gap-3">
                                {/* Search */}
                                <div className="relative flex-shrink-0 w-full sm:w-48">
                                    <input
                                        type="text"
                                        value={menuSearchQuery}
                                        onChange={(e) => setMenuSearchQuery(e.target.value)}
                                        placeholder="Search menu..."
                                        className="w-full h-full px-3 py-2 pl-8 border border-gray-300 rounded-none text-xs focus:outline-none focus:border-[#C9A687] bg-white"
                                    />
                                    <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                                    {menuSearchQuery && (
                                        <button
                                            onClick={() => setMenuSearchQuery('')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm leading-none"
                                        >✕</button>
                                    )}
                                </div>
                                {/* Tabs */}
                                <div className="grid grid-cols-3 gap-2 sm:gap-3 flex-1">
                                    {tabs.map(tab => (
                                        <button
                                            key={tab.name}
                                            onClick={() => handleTabClick(tab.name)}
                                            className={`py-1.5 px-1 rounded-none border flex flex-col items-center justify-center gap-0.5 transition-all ${activeTab === tab.name
                                                    ? 'bg-[#303030] border-[#303030] text-white shadow-sm'
                                                    : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                                                }`}
                                        >
                                            <span className="text-base">{tab.icon}</span>
                                            <span className="text-[10px] font-medium">{tab.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Bottom Action Buttons */}
                        <div className="bg-[#FAF8F6] px-6 pb-6 flex justify-between gap-3">
                            <button
                                onClick={() => { setCartItems([]); setAppliedDiscount(null); setLoyaltyCustomer(null); setLoyaltyQuery(''); setLoyaltyResult(null); setEmailCheckDone(false) }}
                                className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-none font-medium hover:bg-red-100 text-xs"
                            >
                                Clear Cart
                            </button>
                            <button
                                onClick={() => {
                                    if (!selectedBarista) {
                                        Swal.fire({ icon: 'warning', title: 'No Barista Selected', text: 'Select a barista before managing the cash drawer.', confirmButtonColor: '#A0826D' })
                                        return
                                    }
                                    setShowCashDrawerModal(true)
                                }}
                                className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-none font-medium hover:bg-gray-50 text-xs flex items-center gap-1.5"
                            >
                                {cashDrawerSession && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
                                Cash Drawer
                            </button>
                        </div>
                    </div>

                    {/* Checkout Panel */}
                    <div className="w-full lg:w-96 lg:border-l border-t lg:border-t-0 border-gray-200 bg-white flex flex-col max-h-[45vh] lg:max-h-none">
                        {activeRemakeRequest && (
                            <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-bold text-red-600">REMAKE ORDER</p>
                                    <p className="text-[11px] text-red-500">Original Order: #{activeRemakeRequest.reference_number || activeRemakeRequest.order_id}</p>
                                </div>
                                <button onClick={handleCancelRemakeMode} className="text-xs text-red-400 hover:text-red-600">Cancel</button>
                            </div>
                        )}
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-semibold text-gray-800">Checkout</h2>
                                {cartItems.some(c => (c.line_status || 'active') === 'active') && (
                                    <button onClick={() => { setVoidSelectionMode(v => !v); setSelectedVoidKeys(new Set()) }}
                                        className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${voidSelectionMode ? 'bg-red-50 border-red-300 text-red-600' : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'}`}>
                                        {voidSelectionMode ? 'Cancel Selection' : 'Select to Void'}
                                    </button>
                                )}
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-sm font-medium text-gray-600">
                                <div>Name</div>
                                <div className="text-center">QTY</div>
                                <div className="text-right">Price</div>
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto p-4 space-y-3">
                            {cartItems.length === 0 ? (
                                <div className="text-center text-gray-400 text-sm mt-8">No items in cart</div>
                            ) : (
                                cartItems.map(item => {
                                    const isExpanded = expandedCartKeys.has(item.cartKey)
                                    const currentStatus = item.line_status || 'active'
                                    const statusMeta = getLineStatusMeta(currentStatus)
                                    const isLocked = currentStatus === 'void_requested' || currentStatus === 'voided'
                                    return (
                                        <div key={item.cartKey} className="py-2 border-b border-gray-100">
                                            <div className="flex items-start gap-2">
                                                {voidSelectionMode && (item.line_status || 'active') === 'active' && (
                                                    <input type="checkbox" className="mt-1 accent-red-500 cursor-pointer" checked={selectedVoidKeys.has(item.cartKey)}
                                                        onChange={(e) => { setSelectedVoidKeys(prev => { const next = new Set(prev); e.target.checked ? next.add(item.cartKey) : next.delete(item.cartKey); return next }) }} />
                                                )}
                                                <div className="flex-1">
                                                    <div className="grid grid-cols-3 gap-2 items-start">
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-800 leading-tight">{item.name}</p>
                                                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusMeta.className}`}>{statusMeta.label}</span>
                                                                {item.void_reason && currentStatus === 'void_requested' && <span className="text-[10px] text-gray-500">Reason: {item.void_reason.replace(/_/g, ' ')}</span>}
                                                            </div>
                                                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                                                                <button onClick={() => handleEditCartItem(item)} disabled={isLocked} className="text-xs text-[#A0826D] hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed">Edit</button>
                                                                <button onClick={() => handleToggleCartDetails(item.cartKey)} className="text-xs text-gray-500 hover:underline">{isExpanded ? 'Hide Details' : 'Details'}</button>
                                                                <button onClick={() => handleVoidItem(item)} disabled={(item.line_status || 'active') !== 'active'} className="text-xs text-red-500 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed">Void</button>
                                                            </div>
                                                            {isExpanded && (
                                                                <div className="mt-1 space-y-1">
                                                                    {item.variants.length > 0 && <p className="text-xs text-gray-400">{item.variants.join(', ')}</p>}
                                                                    {item.modifiers.length > 0 && <p className="text-xs text-[#C9A687]">+{item.modifiers.join(', ')}</p>}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center justify-center gap-1 mt-0.5">
                                                            <button onClick={() => handleChangeQty(item.cartKey, -1)} disabled={isLocked || (parseInt(item.qty, 10) || 1) <= 1} className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-bold flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed">−</button>
                                                            <input type="text" inputMode="numeric" pattern="[0-9]*" value={item.qty} disabled={isLocked} onChange={(e) => handleQtyInputChange(item.cartKey, e.target.value)} onBlur={() => handleQtyInputBlur(item.cartKey)} className="w-10 h-7 border border-gray-300 rounded text-center text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-[#C9A687] disabled:bg-gray-100 disabled:text-gray-400" />
                                                            <button onClick={() => handleChangeQty(item.cartKey, 1)} disabled={isLocked || (parseInt(item.qty, 10) || 1) >= 99} className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-bold flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed">+</button>
                                                        </div>
                                                        <span className="text-sm text-green-600 font-semibold text-right mt-0.5">₱{(item.unitPrice * (parseInt(item.qty, 10) || 0)).toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })
                            )}
                        </div>

                        <div className="px-5 py-3 border-t border-gray-200 space-y-3">
                            {cartItems.length > 0 && (
                                <>
                                    <div className="flex justify-between text-sm text-gray-600"><span>Subtotal</span><span>₱{subtotal.toFixed(2)}</span></div>
                                    <div className="flex justify-between items-start text-sm text-gray-600">
                                        <div className="flex flex-col">
                                            <span>Discount</span>
                                            {appliedDiscount ? (
                                                <div className="mt-1 inline-flex items-center gap-2">
                                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-md font-medium">{appliedDiscount.label}</span>
                                                    <button onClick={() => setAppliedDiscount(null)} className="text-xs text-gray-400 hover:text-red-500">×</button>
                                                </div>
                                            ) : (
                                                <button onClick={() => setShowDiscountModal(true)} className="mt-1 text-xs text-[#A0826D] hover:underline text-left">
                                                    Add Discount / Earn Points
                                                </button>
                                            )}
                                        </div>
                                        <span>-₱{discount.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm text-gray-500"><span>VAT (12%) Included</span><span>₱{vat.toFixed(2)}</span></div>
                                    <div className="flex justify-between text-lg font-bold text-gray-800 pt-2 border-t"><span>Total</span><span className="text-green-600">₱{total.toFixed(2)}</span></div>
                                    {loyaltyCustomer && (
                                        <div className="flex justify-between items-center text-sm text-gray-600">
                                            <div className="flex flex-col">
                                                <span>Loyalty</span>
                                                <div className="mt-1 inline-flex items-center gap-2">
                                                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-md font-medium">
                                                        {loyaltyCustomer.name}
                                                    </span>
                                                    <button onClick={() => { setLoyaltyCustomer(null); setLoyaltyQuery(''); setLoyaltyResult(null); setEmailCheckDone(false) }} className="text-xs text-gray-400 hover:text-red-500">×</button>
                                                </div>
                                            </div>
                                            <span className="text-xs text-amber-600">+{Math.floor(total / 20)} pts</span>
                                        </div>
                                    )}
                                </>
                            )}
                            {voidSelectionMode && (
                                <button onClick={handleVoidSelected} disabled={selectedVoidKeys.size === 0} className="w-full py-3 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
                                    Void Selected ({selectedVoidKeys.size})
                                </button>
                            )}
                            <button onClick={handlePay} disabled={cartItems.length === 0 || payableItemCount === 0} className="w-full py-4 bg-orange-600 text-white rounded-none text-base font-semibold hover:bg-[#b8956f] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                                Pay
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Show Discount Modal */}
            {showDiscountModal && (
                <DiscountModal
                    subtotal={subtotal}
                    total={total}
                    cartItems={cartItems}
                    menuItems={menuItems}
                    allBranchItems={allBranchItems}
                    appliedDiscount={appliedDiscount}
                    setAppliedDiscount={setAppliedDiscount}
                    setShowDiscountModal={setShowDiscountModal}
                    loyaltyCustomer={loyaltyCustomer}
                    setLoyaltyCustomer={setLoyaltyCustomer}
                    loyaltyQuery={loyaltyQuery}
                    setLoyaltyQuery={setLoyaltyQuery}
                    loyaltyResult={loyaltyResult}
                    setLoyaltyResult={setLoyaltyResult}
                    emailCheckDone={emailCheckDone}
                    setEmailCheckDone={setEmailCheckDone}
                    staffBranchId={staffBranchId}
                    isOnline={isOnline}
                    userData={userData}
                />
            )}

            {showCashDrawerModal && (
                <CashDrawerModal
                    session={cashDrawerSession}
                    transactions={cashDrawerTransactions}
                    staffBranchId={staffBranchId}
                    baristaId={selectedBarista?.barista_id}
                    baristaName={selectedBarista ? `${selectedBarista.first_name} ${selectedBarista.last_name}` : null}
                    onClose={() => setShowCashDrawerModal(false)}
                    onSessionChange={(session, transactions) => {
                        setCashDrawerSession(session)
                        setCashDrawerTransactions(transactions)
                        // Keep the offline cache in sync the instant any change
                        // happens — online or offline — so a connectivity drop
                        // right after an online action doesn't leave the cache
                        // stale (which is what let a 2nd drawer get opened).
                        cacheCashDrawerSession(staffBranchId, session)
                        if (session) cacheCashDrawerTransactions(session.session_id, transactions)
                    }}
                    isOnline={isOnline}
                />
            )}


            {showRemakeListModal && (
                <div className="fixed inset-0 z-[65] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowRemakeListModal(false)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[85vh]">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h2 className="text-base font-semibold text-gray-800">Remake Requests ({remakeRequests.length})</h2>
                            <button onClick={() => setShowRemakeListModal(false)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-sm">✕</button>
                        </div>
                        <div className="overflow-y-auto flex-1 p-4 space-y-2">
                            {remakeRequests.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-10">No pending remake requests.</p>
                            ) : (
                                remakeRequests.map(r => (
                                    <button key={r.remake_request_id} onClick={() => handleOpenRemakeProcessing(r)}
                                        className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-[#C9A687] transition-colors">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-semibold text-gray-800">#{r.reference_number || r.order_id}</span>
                                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {r.status === 'pending' ? 'Pending' : 'In Progress'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">Reason: {r.reason}</p>
                                        {r.remake_items?.length > 0 && (
                                            <p className="text-xs text-orange-600 mt-0.5">
                                                Items to remake: {r.remake_items.map(i => `${i.name} ×${i.quantity}`).join(', ')}
                                            </p>
                                        )}
                                        <p className="text-xs text-gray-400 mt-0.5">
                                            Responsible: {(r.responsible_staff || []).map(s => `${s.name} (${s.role})`).join(', ') || '—'}
                                        </p>
                                        <p className="text-[11px] text-gray-400 mt-0.5">Requested {new Date(r.created_at).toLocaleString('en-PH')}</p>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showRemakeItemSelectModal && (
                <div className="fixed inset-0 z-[66] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={handleCancelRemakeItemSelection} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[85vh]">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <div>
                                <h2 className="text-base font-semibold text-gray-800">Select Items to Remake</h2>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Order #{pendingRemakeRequest?.reference_number || pendingRemakeRequest?.order_id}
                                </p>
                            </div>
                            <button onClick={handleCancelRemakeItemSelection} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-sm">✕</button>
                        </div>

                        <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
                            <p className="text-xs text-gray-500">
                                {selectedRemakeCandidateIds.size} of {remakeCandidateItems.length} item(s) selected — only these will be loaded into the cart.
                            </p>
                            <button
                                type="button"
                                onClick={handleToggleSelectAllRemakeCandidates}
                                className="text-xs text-[#A0826D] hover:underline flex-shrink-0 ml-3"
                            >
                                {selectedRemakeCandidateIds.size === remakeCandidateItems.length ? 'Deselect all' : 'Select all'}
                            </button>
                        </div>

                        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-2">
                            {remakeCandidateItems.length === 0 ? (
                                <p className="text-xs text-gray-400 text-center py-6">No items found for this request.</p>
                            ) : (
                                remakeCandidateItems.map(item => {
                                    const isChecked = selectedRemakeCandidateIds.has(item.order_item_id)
                                    return (
                                        <label key={item.order_item_id}
                                            className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${isChecked ? 'border-[#C9A687] bg-[#C9A687]/5' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => handleToggleRemakeCandidate(item.order_item_id)}
                                                className="mt-0.5 accent-[#A0826D]"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm font-medium leading-tight ${isChecked ? 'text-gray-800' : 'text-gray-500'}`}>
                                                    {item.name} × {item.qty}
                                                </p>
                                                {item.variants?.length > 0 && <p className="text-xs text-gray-400">{item.variants.join(' · ')}</p>}
                                                {item.modifiers?.length > 0 && <p className="text-xs text-[#C9A687]">+{item.modifiers.join(', ')}</p>}
                                            </div>
                                            <span className="text-sm font-semibold text-gray-700 flex-shrink-0">₱{Number(item.unitPrice).toFixed(2)}</span>
                                        </label>
                                    )
                                })
                            )}
                        </div>

                        <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
                            <button onClick={handleCancelRemakeItemSelection}
                                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">
                                Cancel
                            </button>
                            <button onClick={handleConfirmRemakeItemSelection}
                                className="px-5 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700">
                                Load Selected Items
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            {showPaymentModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <h2 className="text-lg font-bold mb-4 text-gray-800">Payment</h2>
                        <div className="space-y-4">

                            {/* Order Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Order Type</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {[{ value: 'dine_in', label: 'Dine-In' }, { value: 'takeout', label: 'Takeout' }].map(type => (
                                        <button key={type.value} type="button" onClick={() => setSelectedFulfillmentType(type.value)}
                                            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${selectedFulfillmentType === type.value ? 'bg-[#C9A687]/15 border-[#C9A687] text-[#8B6344]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                            {type.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Split toggle */}
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">Split Payment</label>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsSplitPayment(v => !v)
                                        setSplitPayments([])
                                    }}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isSplitPayment ? 'bg-[#C9A687]' : 'bg-gray-300'}`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isSplitPayment ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>

                            {/* SINGLE PAYMENT */}
                            {!isSplitPayment && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { value: 'cash', label: 'Cash' },
                                                { value: 'gcash', label: 'GCash' },
                                                { value: 'card', label: 'Credit/Debit Card' },
                                                { value: 'bank_transfer', label: 'Bank Transfer' },
                                                { value: 'managers_pullout', label: "Manager's Pull-Out" },
                                                { value: 'marketing_pullout', label: 'Marketing Pull-Out' },
                                            ].map(method => (
                                                <button key={method.value} type="button" onClick={() => setSelectedPaymentMethod(method.value)}
                                                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${selectedPaymentMethod === method.value ? 'bg-[#C9A687]/15 border-[#C9A687] text-[#8B6344]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                                    {method.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                                        <div className="flex justify-between"><span className="text-gray-600">Total Due</span><span className="font-semibold text-gray-800">₱{total.toFixed(2)}</span></div>
                                        {selectedPaymentMethod === 'cash' && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount Received</label>
                                                    <input type="number" value={amountReceived} onChange={e => setAmountReceived(e.target.value)}
                                                        onKeyDown={e => { if (!/[0-9]|Backspace|Delete|ArrowLeft|ArrowRight|Tab/.test(e.key)) e.preventDefault() }}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                                                </div>
                                                <div className="flex justify-between"><span className="text-gray-600">Change</span><span className="font-semibold text-gray-800">₱{changeAmount.toFixed(2)}</span></div>
                                            </>
                                        )}
                                        {['gcash', 'card', 'bank_transfer'].includes(selectedPaymentMethod) && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number <span className="text-red-500">*</span></label>
                                                <input type="text" value={paymentReferenceNumber} onChange={e => setPaymentReferenceNumber(e.target.value)}
                                                    placeholder="e.g. GCash ref, card approval code, bank transfer ref..."
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A687]" />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* SPLIT PAYMENT */}
                            {isSplitPayment && (
                                <div className="space-y-3">
                                    <div className="bg-gray-50 rounded-lg p-3 flex justify-between text-sm">
                                        <span className="text-gray-600">Total Due</span>
                                        <span className="font-semibold">₱{total.toFixed(2)}</span>
                                    </div>

                                    {splitPayments.map((sp, idx) => (
                                        <div key={idx} className="border border-gray-200 rounded-lg p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-semibold text-gray-600">Payment {idx + 1}</span>
                                                <button onClick={() => setSplitPayments(prev => prev.filter((_, i) => i !== idx))}
                                                    className="text-xs text-red-400 hover:text-red-600">Remove</button>
                                            </div>
                                            <div className="grid grid-cols-3 gap-1.5">
                                                {[
                                                    { value: 'cash', label: 'Cash' },
                                                    { value: 'gcash', label: 'GCash' },
                                                    { value: 'card', label: 'Credit/Debit' },
                                                    { value: 'bank_transfer', label: 'Bank Transfer' },
                                                    { value: 'managers_pullout', label: "Mgr. P/O" },
                                                    { value: 'marketing_pullout', label: "Mktg. P/O" },
                                                ].map(method => (
                                                    <button key={method.value} type="button"
                                                        onClick={() => setSplitPayments(prev => prev.map((s, i) => i === idx ? { ...s, method: method.value, referenceNumber: '' } : s))}
                                                        className={`px-2 py-1.5 rounded border text-xs font-medium transition-colors ${sp.method === method.value ? 'bg-[#C9A687]/15 border-[#C9A687] text-[#8B6344]' : 'bg-white border-gray-300 text-gray-700'}`}>
                                                        {method.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-500 mb-1 block">Amount</label>
                                                <input type="number" value={sp.amount}
                                                    onChange={e => setSplitPayments(prev => prev.map((s, i) => i === idx ? { ...s, amount: e.target.value } : s))}
                                                    placeholder="0.00"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A687]" />
                                            </div>
                                            {sp.method === 'cash' && (
                                                <p className="text-xs text-gray-500">
                                                    Change: ₱{Math.max(0, (parseFloat(sp.amount) || 0) - Math.max(0, splitRemaining + (parseFloat(sp.amount) || 0) - (total - splitPayments.filter((_, i) => i !== idx).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0)))).toFixed(2)}
                                                </p>
                                            )}
                                           {['gcash', 'card', 'bank_transfer'].includes(sp.method) && (
                                                <div>
                                                    <label className="text-xs text-gray-500 mb-1 block">Reference Number <span className="text-red-500">*</span></label>
                                                    <input type="text" value={sp.referenceNumber}
                                                        onChange={e => setSplitPayments(prev => prev.map((s, i) => i === idx ? { ...s, referenceNumber: e.target.value } : s))}
                                                        placeholder="e.g. GCash ref, bank transfer ref..."
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#C9A687]" />
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    <button
                                        onClick={() => setSplitPayments(prev => [...prev, { method: 'cash', amount: splitRemaining > 0 ? splitRemaining.toFixed(2) : '', referenceNumber: '' }])}
                                        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-[#C9A687] hover:text-[#A0826D] transition-colors"
                                    >
                                        + Add payment method
                                    </button>

                                    <div className={`rounded-lg p-3 flex justify-between text-sm font-semibold ${splitRemaining > 0 ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                                        <span>{splitRemaining > 0 ? 'Remaining' : 'Fully covered'}</span>
                                        <span>{splitRemaining > 0 ? `-₱${splitRemaining.toFixed(2)}` : '✓'}</span>
                                    </div>
                                </div>
                            )}

                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={() => {
                                setShowPaymentModal(false); setAmountReceived(''); setSelectedPaymentMethod('cash')
                                setSelectedFulfillmentType('dine_in'); setPaymentReferenceNumber('')
                                setIsSplitPayment(false); setSplitPayments([])
                            }}>Cancel</button>
                            <button onClick={handleConfirmCheckout} disabled={isProcessingPayment} className="px-4 py-2 bg-[#FF6F3C] text-white rounded-lg">
                                {isProcessingPayment ? 'Processing...' : 'Confirm Payment'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Pin Modal */}
            {showPinModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-sm">
                        <h2 className="text-lg font-bold mb-4">Admin Authorization</h2>
                        <input type="password" inputMode="numeric" value={enteredPin} onChange={(e) => setEnteredPin(e.target.value.replace(/\D/g, '').slice(0, 6))} className="w-full px-4 py-2 border rounded-lg mb-4" placeholder="Enter Admin PIN" />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setShowPinModal(false); setEnteredPin(''); setPendingVoidItem(null) }} className="px-4 py-2 bg-gray-300 rounded-lg">Cancel</button>
                            <button onClick={verifyAdminPin} className="px-4 py-2 bg-[#FF6F3C] text-white rounded-lg">Confirm</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Receipt & Sticker Modal */}
            {showReceiptModal && completedOrder && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">

                        {/* Header */}
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">Order Complete</h2>
                                <p className="text-xs text-gray-400 mt-0.5">#{completedOrder.referenceNumber}</p>
                            </div>
                            <button
                                onClick={() => { setShowReceiptModal(false); setCompletedOrder(null) }}
                                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-sm"
                            >✕</button>
                        </div>

                        {/* Tab switcher */}
                        <div className="flex gap-1 px-6 pt-4">
                            <button
                                onClick={() => setReceiptView('receipt')}
                                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${receiptView === 'receipt' ? 'bg-[#303030] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                                Receipt
                            </button>
                            <button
                                onClick={() => setReceiptView('sticker')}
                                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${receiptView === 'sticker' ? 'bg-[#303030] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                                Sticker
                            </button>
                        </div>

                        {/* Receipt View */}
                        {receiptView === 'receipt' && (
                            <div className="flex-1 overflow-y-auto px-6 py-4">
                                {/* Hidden printable receipt */}
                                <ReceiptLayout
                                    order={completedOrder}
                                    items={completedOrder.items}
                                    transaction={null}
                                    branchName=""
                                    branchAddress={completedOrder.branchAddress}
                                    cashier={completedOrder.cashier}
                                />

                                {/* Visual preview in modal */}
                                <div className="mt-4 bg-gray-50 rounded-xl p-4 text-center">
                                    <p className="text-xs text-gray-400 mb-1">Receipt preview above will print to your connected printer.</p>
                                    <p className="text-xs text-gray-400">Make sure your printer is set as the default printer in your OS Bluetooth/printer settings.</p>
                                </div>
                            </div>
                        )}

                        {/* Sticker View */}
                        {receiptView === 'sticker' && (
                            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                                <p className="text-sm text-gray-500">Select items to print stickers for:</p>
                                <div className="space-y-2">
                                    {completedOrder.items.map((item, i) => (
                                        <label key={i} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${selectedStickerItems.has(item.cartKey) ? 'border-[#C9A687] bg-[#C9A687]/5' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                                            <input
                                                type="checkbox"
                                                checked={selectedStickerItems.has(item.cartKey)}
                                                onChange={() => toggleStickerItem(item.cartKey)}
                                                className="mt-0.5 accent-[#A0826D]"
                                            />
                                            <div className="flex-1">
                                                <p className="text-sm font-semibold text-gray-800">{item.name}</p>
                                                {item.variants?.length > 0 && <p className="text-xs text-gray-500">{item.variants.join(' · ')}</p>}
                                                {item.modifiers?.length > 0 && <p className="text-xs text-[#A0826D]">+{item.modifiers.join(', ')}</p>}
                                                <p className="text-xs text-gray-400 mt-0.5">Qty: {item.qty}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>

                                {/* Hidden sticker print content */}
                                <div id="sticker-content" style={{ display: 'none' }}>
                                    {completedOrder.items
                                        .filter(item => selectedStickerItems.has(item.cartKey))
                                        .flatMap((item) =>
                                            Array.from({ length: parseInt(item.qty, 10) || 1 }, (_, qtyIdx) => (
                                                <div key={`${item.cartKey}-${qtyIdx}`} style={{
                                                    width: '50mm', padding: '4mm', marginBottom: '2mm',
                                                    border: '1px solid #ccc', fontFamily: 'monospace',
                                                    fontSize: 11, pageBreakAfter: 'always'
                                                }}>
                                                    <div style={{ fontWeight: 'bold', fontSize: 13 }}>{item.name}</div>
                                                    {item.variants?.length > 0 && <div style={{ color: '#555', marginTop: 2 }}>{item.variants.join(' · ')}</div>}
                                                    {item.modifiers?.length > 0 && <div style={{ color: '#A0826D', marginTop: 2 }}>+{item.modifiers.join(', ')}</div>}
                                                    <div style={{ marginTop: 4, color: '#888', fontSize: 10 }}>
                                                        #{completedOrder.referenceNumber} · {completedOrder.fulfillmentType === 'dine_in' ? 'Dine-In' : 'Takeout'}
                                                    </div>
                                                </div>
                                            ))
                                        )
                                    }
                                </div>

                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                                    <p className="text-xs text-amber-700 font-medium">Sticker Printer Setup</p>
                                    <p className="text-xs text-amber-600 mt-1">Connect your sticker printer (e.g. Brother QL, DYMO) via Bluetooth in your OS settings and set it as the default printer before printing stickers. Each item qty prints as a separate sticker.</p>
                                </div>
                            </div>
                        )}

                        {/* Footer actions */}
                        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
                            <button
                                onClick={() => { setShowReceiptModal(false); setCompletedOrder(null) }}
                                className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold text-sm hover:bg-gray-200 transition-colors"
                            >
                                Skip
                            </button>
                            {receiptView === 'receipt' ? (
                                <button
                                    onClick={handlePrintReceipt}
                                    className="flex-1 py-2.5 rounded-xl bg-[#303030] text-white font-semibold text-sm hover:bg-black transition-colors"
                                >
                                    Print Receipt
                                </button>
                            ) : (
                                <button
                                    onClick={handlePrintStickers}
                                    disabled={selectedStickerItems.size === 0}
                                    className="flex-1 py-2.5 rounded-xl bg-[#A0826D] text-white font-semibold text-sm hover:bg-[#8c705b] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    Print {selectedStickerItems.size} Sticker{selectedStickerItems.size !== 1 ? 's' : ''}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}